import { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import api, { money } from "../../lib/api";
import { useSupplier } from "../../context/SupplierContext";
import HubModeNotice from "../../components/HubModeNotice";
import SupplierFilterPills from "../../components/SupplierFilterPills";
import ClaimsTab from "../../components/ClaimsTab";
import InvoiceExpiryChip from "../../components/InvoiceExpiryChip";
import { BuyerChangeRequestBanner } from "../../components/FulfillmentChangeRequest";
import { Button } from "../../components/ui/button";
import { SAMPLE_ORDERS } from "../../lib/sampleData";
import { SamplePreviewBanner, SampleChip, SampleOrderDrawer } from "../../components/SamplePreview";
import { Input } from "../../components/ui/input";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "../../components/ui/sheet";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../../components/ui/dialog";
import SearchAndFilter from "../../components/SearchAndFilter";
import SellerPoliciesPanel from "../../components/SellerPoliciesPanel";
import { toast } from "sonner";
import {
  CheckCircle2, Circle, Clock, CreditCard, Package, Truck, Check, X,
  Search, Upload, ShoppingBag, Repeat, Download, Shield, FileText,
  Sparkles, ArrowRight, ArrowUpRight, FileUp, ArrowLeft, Activity, Zap,
  DollarSign, CalendarDays, Store, Receipt, MapPin, AlertTriangle,
} from "lucide-react";

// Compute which of the 6 journey steps an order is in
function getStage(order, invoice) {
  if (order.status === "delivered") return "delivered";
  if (order.status === "transit") return "shipped";
  // ready_for_pickup is its own stage — buyer needs to schedule a carrier.
  // Fix (Feb 2026): previously fell through to "seller_confirm" causing
  // misclassification. Now drives the new "Schedule pickup" tab.
  if (order.status === "ready_for_pickup") return "ready_for_pickup";
  if (invoice?.status === "paid" && order.status === "processing") return "pack";
  if (invoice && invoice.status !== "paid" && invoice.status !== "draft") return "pay";
  if (order.status === "pending" || invoice?.status === "draft") return "seller_confirm";
  return "seller_confirm";
}

const STAGE_META = {
  seller_confirm:   { num: 3, label: "Seller Confirms",  icon: Clock,      chip: "bg-amber-100 text-amber-800",  emoji: "⏳", actionLabel: "View" },
  pay:              { num: 4, label: "Pay Invoice",      icon: CreditCard, chip: "bg-rose-100 text-rose-800",     emoji: "💳", actionLabel: "Pay now" },
  pack:             { num: 5, label: "Seller Packs",     icon: Package,    chip: "bg-blue-100 text-blue-800",     emoji: "📦", actionLabel: "Status" },
  ready_for_pickup: { num: 6, label: "Schedule pickup",  icon: Truck,      chip: "bg-amber-100 text-amber-800",   emoji: "🚛", actionLabel: "Book carrier" },
  shipped:          { num: 6, label: "Shipped",          icon: Truck,      chip: "bg-indigo-100 text-indigo-800", emoji: "🚚", actionLabel: "Track" },
  delivered:        { num: 7, label: "Delivered",        icon: Check,      chip: "bg-accent/30 border border-accent/40", emoji: "✓", actionLabel: "View" },
};

// ─────────────────────────────────────────────────────────────────────────────
// LEAN-LAYOUT HELPERS (Feb 2026 redesign) — same logic as SellerOrders.jsx.
// ─────────────────────────────────────────────────────────────────────────────
function stageEnteredAt(order) {
  const stamps = [
    order?.last_adjusted_at,
    order?.shipment?.delivered_at,
    order?.shipment?.shipped_at,
    order?.shipment?.created_at,
    order?.invoice?.paid_at,
    order?.invoice?.issued_at,
    order?.invoice?.created_at,
    order?.confirmed_at,
    order?.created_at,
  ].filter(Boolean).map(t => new Date(t).getTime()).filter(Number.isFinite);
  return stamps.length ? Math.max(...stamps) : null;
}

function ageInStageLabel(order) {
  const t = stageEnteredAt(order);
  if (!t) return "—";
  const ms = Date.now() - t;
  if (ms < 60_000) return "now";
  const m = Math.floor(ms / 60_000);
  if (m < 60) return `${m}m`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h`;
  const d = Math.floor(h / 24);
  return `${d}d`;
}

function ageSlaTone(order, view) {
  const t = stageEnteredAt(order);
  if (!t) return null;
  const hrs = (Date.now() - t) / 3_600_000;
  if (view === "action") {
    if (hrs > 48) return "rose";
    if (hrs > 24) return "amber";
  } else if (view === "progress") {
    if (hrs > 24 * 10) return "rose";
    if (hrs > 24 * 5)  return "amber";
  }
  return null;
}

const ALL_STEPS = [
  { id: "place", num: 1, label: "Place Order", desc: "Browse or upload PO" },
  { id: "cart",  num: 2, label: "Confirm Order", desc: "Review & submit" },
  { id: "seller_confirm", num: 3, label: "Seller Confirms", desc: "Awaiting approval" },
  { id: "pay",   num: 4, label: "Pay Invoice", desc: "Pay to start packing" },
  { id: "pack",  num: 5, label: "Seller Packs", desc: "Pick & pack" },
  { id: "shipped", num: 6, label: "Schedule Shipping", desc: "Book a carrier" },
];

// 6 consolidated tabs (May 2026 redesign · mirrors SellerOrders pattern
// where applicable, but preserves the buyer-only "Journey" step-by-step
// educational flow as the default view).
// "Action needed" merges the old "To pay" + "Schedule pickup" views since
// both are buyer-side action items. Old hashes (`pay`, `pickup`, `confirm`,
// `pack`, `transit`) are accepted via VALID_VIEWS and 301-redirected to
// the new merged tabs in the redirect-effect below.
const VALID_VIEWS = [
  "journey", "all", "action", "progress", "done", "claims",
  // legacy aliases (auto-redirected)
  "pay", "pickup", "confirm", "pack", "transit",
];
const VIEW_LABEL = {
  journey:  "Journey",
  all:      "All orders",
  action:   "Action needed",
  progress: "In progress",
  done:     "Delivered",
  claims:   "Claims",
};
// Map each view to the underlying STAGE_META keys it includes
const VIEW_STAGES = {
  action:   ["pay", "ready_for_pickup"],
  progress: ["seller_confirm", "pack", "shipped"],
  done:     ["delivered"],
};
const LEGACY_VIEW_REMAP = {
  pay:     "action",
  pickup:  "action",
  confirm: "progress",
  pack:    "progress",
  transit: "progress",
};

// Pill-tab order with icon + count key (used in the page-level filter bar)
const VIEW_TABS = [
  { key: "journey",  label: "Journey",        icon: Sparkles,      countKey: null },
  { key: "all",      label: "All",            icon: FileText,      countKey: "all" },
  { key: "action",   label: "Action needed",  icon: Zap,           countKey: "action",   tone: "amber",  action: true },
  { key: "progress", label: "In progress",    icon: Activity,      countKey: "progress", tone: "blue" },
  { key: "done",     label: "Delivered",      icon: Check,         countKey: "delivered",tone: "emerald" },
  { key: "claims",   label: "Claims",         icon: AlertTriangle, countKey: "claims",   tone: "rose" },
];

export default function BuyerOrders() {
  const nav = useNavigate();
  const loc = useLocation();
  const view = useMemo(() => {
    const h = (loc.hash || "").replace("#", "");
    return VALID_VIEWS.includes(h) ? h : "journey";
  }, [loc.hash]);

  // Legacy hash redirect — old links like /buyer/orders#pack quietly land
  // on the merged "In progress" tab without a flash of the old empty view.
  useEffect(() => {
    const h = (loc.hash || "").replace("#", "");
    if (LEGACY_VIEW_REMAP[h]) {
      nav(`/buyer/orders#${LEGACY_VIEW_REMAP[h]}`, { replace: true });
    }
  }, [loc.hash, nav]);

  const [orders, setOrders] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [shipments, setShipments] = useState([]);
  const [installedApps, setInstalledApps] = useState([]);
  const [supplierFeatures, setSupplierFeatures] = useState({}); // {catalog_enabled, supplier_apps: [{seller_id, seller_name, app_ids}]}
  const [cap, setCap] = useState(null); // capital summary — drives auto-debit/credit visibility
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [drawer, setDrawer] = useState(null);
  const [poOpen, setPoOpen] = useState(false);
  const [poParsing, setPoParsing] = useState(false);
  const [poResult, setPoResult] = useState(null);
  const [poFile, setPoFile] = useState(null);
  const [poDragOver, setPoDragOver] = useState(false);
  const [poDefaultSellerId, setPoDefaultSellerId] = useState(""); // supplier for unmatched items
  const [poSubmitting, setPoSubmitting] = useState(false);
  const [poShippingMode, setPoShippingMode] = useState("traqo"); // 'traqo' | 'seller' | 'buyer_arranged'
  const [poWaiverAccepted, setPoWaiverAccepted] = useState(false);
  // ── Post-prep "Change shipping method" modal (buyer-only) ────────────
  // Lets the buyer swap shipping handler after the seller has prepared
  // the order, up until it's marked shipped/released. See PATCH
  // /api/orders/{oid}/shipping-mode for the rules.
  const [changeShipOpen, setChangeShipOpen] = useState(false);
  const [changeShipMode, setChangeShipMode] = useState("traqo");
  const [changeShipWaiver, setChangeShipWaiver] = useState(false);
  const [changeShipSaving, setChangeShipSaving] = useState(false);
  // ── Buyer-initiated cancel modal (with restocking-fee preview) ─────────
  const [cancelOpen, setCancelOpen] = useState(false);
  const [cancelPreview, setCancelPreview] = useState(null);   // {order_total, restocking_fee, refund_amount, was_packed, restocking_pct}
  const [cancelReason, setCancelReason] = useState("");
  const [cancelSaving, setCancelSaving] = useState(false);
  // ── Prebook flow (deposit + balance) ────────────────────────────────
  // Three buyer actions: accept the offer (pay deposit), decline, or cancel
  // overdue (instant refund). Extensions are handled inline via decide buttons.
  const [prebookBusy, setPrebookBusy] = useState(false);
  // Single-pane journey: which step is in focus right now
  const [activeStep, setActiveStep] = useState("place");
  const [stepInited, setStepInited] = useState(false); // only auto-detect once
  const [filters, setFilters] = useState({});          // composable facet filters

  // Reset pagination when switching views
  useEffect(() => { setPage(1); }, [view, filters]);

  const [initialLoaded, setInitialLoaded] = useState(false);
  const load = () => Promise.all([
    api.get("/orders"), api.get("/invoices"), api.get("/shipping"), api.get("/apps"),
    api.get("/capital/summary"),
    // Supplier-derived feature flags — uses the beta-allowlist filter so
    // non-beta sellers never surface the Catalog app tile.
    api.get("/buyer/supplier-features").catch(() => ({ data: { catalog_enabled: false } })),
  ]).then(([o, i, s, a, c, f]) => {
    setOrders(o.data); setInvoices(i.data); setShipments(s.data);
    setInstalledApps(a.data.filter(x => x.installed).map(x => x.id));
    setCap(c.data);
    setSupplierFeatures(f.data || {});
  }).finally(() => setInitialLoaded(true));
  useEffect(() => { load(); }, []);
  // Iter 157 · Phase 6 — auto-refresh every 30s so cross-supplier state
  // (paid invoices, shipment status flips) stays current without a
  // manual reload. Cheap — the 6 endpoints are all GET + cached.
  useEffect(() => {
    const t = setInterval(load, 30000);
    return () => clearInterval(t);
  }, []);

  const hasCredit = (cap?.credit_limit || 0) > 0;

  // Compute stages for all orders — scope to active supplier workspace.
  // Prefer seller_id (stable), fall back to seller_name match (legacy orders).
  const { activeSellerId, activeSellerName, connections, selectSupplier } = useSupplier();

  // hasCatalog — TRUE when the buyer is connected to any supplier that
  // has the Catalog app enabled (gated through the private-beta
  // allowlist server-side). Scoping further by activeSellerId so the
  // tile only shows when the in-scope supplier supports it.
  const hasCatalog = (() => {
    if (!supplierFeatures.catalog_enabled) return false;
    if (!activeSellerId) return true; // Hub mode — at least one supplier has it
    const apps = (supplierFeatures.supplier_apps || []).find(s => s.seller_id === activeSellerId)?.app_ids || [];
    return apps.includes("catalog");
  })();

  const invoiceFor = (oid) => invoices.find(i => i.order_id === oid);
  const shipmentFor = (oid) => shipments.find(s => s.order_id === oid);

  // If the buyer had picked "Seller arranges" but the active supplier has
  // opted out of seller-arranged shipping, fall back to the recommended
  // verqo option so the selection always matches a visible card. Must run
  // *after* useSupplier() destructure to avoid a temporal-dead-zone error.
  useEffect(() => {
    if (poShippingMode !== "seller") return;
    const activeConn = activeSellerId && (connections || []).find(c => c.tenant_owner_user_id === activeSellerId);
    if (activeConn && !activeConn.offer_seller_arranged_shipping) {
      setPoShippingMode("traqo");
    }
  }, [poShippingMode, activeSellerId, connections]);
  // Hub mode: every visible order is "elsewhere" — buyer monitors only;
  // actions get redirected into the matching supplier's storefront.
  const inHubMode = !activeSellerId;
  // Map seller_id → connection for fast lookup when redirecting in Hub mode.
  const connectionsBySellerId = useMemo(() => {
    const m = new Map();
    (connections || []).forEach(c => {
      if (c.tenant_owner_user_id) m.set(c.tenant_owner_user_id, c);
    });
    return m;
  }, [connections]);
  // Switch the buyer's active supplier context to the order's seller and
  // navigate them to /buyer/orders inside that storefront. Falls back to a
  // non-blocking toast when the buyer doesn't have an active connection
  // (rare — happens on stale orders for disconnected sellers).
  const openInStorefront = (order) => {
    const conn = connectionsBySellerId.get(order?.seller_id);
    if (!conn) {
      toast.info("This supplier is no longer connected — view-only access.");
      return;
    }
    selectSupplier(conn);
    setDrawer(null);
    nav("/buyer/orders#all");
    toast.success(`Now in ${conn.tenant_name || order.seller_name} · take action below`);
  };
  const staged = useMemo(() => {
    const scopedOrders = activeSellerId
      ? orders.filter(o =>
          o.seller_id === activeSellerId ||
          (o.seller_name || "").toLowerCase() === (activeSellerName || "").toLowerCase()
        )
      : orders;
    return scopedOrders.map(o => {
      const inv = invoiceFor(o.id);
      const sh = shipmentFor(o.id);
      const stage = getStage(o, inv);
      return { ...o, invoice: inv, shipment: sh, stage };
    });
  }, [orders, invoices, shipments, activeSellerId, activeSellerName]);

  // Counts per stage (raw) + derived merged-tab counts
  const counts = useMemo(() => {
    const c = { all: staged.length, seller_confirm: 0, pay: 0, pack: 0, shipped: 0, ready_for_pickup: 0, delivered: 0 };
    staged.forEach(o => { c[o.stage] = (c[o.stage] || 0) + 1; });
    // Merged-tab counts — single source of truth for top tabs + sidebar.
    c.pickup   = c.ready_for_pickup;
    c.progress = c.seller_confirm + c.pack + c.shipped;
    // "Action needed" merges pay + ready_for_pickup (mirrors SellerOrders pattern)
    c.action   = c.pay + c.ready_for_pickup;
    return c;
  }, [staged]);

  // Unique suppliers across this buyer's orders — powers the Supplier facet.
  const supplierOptions = useMemo(() => {
    const seen = new Map();
    staged.forEach(o => {
      if (o.seller_id && !seen.has(o.seller_id)) seen.set(o.seller_id, o.seller_name || o.seller_id);
    });
    return Array.from(seen, ([value, label]) => ({ value, label })).sort((a, b) => a.label.localeCompare(b.label));
  }, [staged]);

  // Auto-pick the most pressing step on first data load. Priority: pay > confirm > pack > shipped > place.
  useEffect(() => {
    if (stepInited || orders.length === 0) return;
    const next =
      counts.pay > 0 ? "pay" :
      counts.seller_confirm > 0 ? "seller_confirm" :
      counts.pack > 0 ? "pack" :
      counts.shipped > 0 ? "shipped" :
      counts.delivered > 0 ? "delivered" :
      "place";
    setActiveStep(next);
    setStepInited(true);
  }, [orders.length, counts, stepInited]);

  // View-driven row set
  const viewFiltered = useMemo(() => {
    let r = staged;
    const stageSet = VIEW_STAGES[view];
    if (stageSet) r = r.filter(o => stageSet.includes(o.stage));
    if (search) r = r.filter(o => JSON.stringify(o).toLowerCase().includes(search.toLowerCase()));

    // ── Composable facet filter overlay ─────────────────────────────────
    const F = filters || {};
    if (F.status?.values?.length)   r = r.filter(o => F.status.values.includes(o.stage));
    if (F.supplier?.values?.length) r = r.filter(o => F.supplier.values.includes(o.seller_id));
    if (F.terms?.values?.length)    r = r.filter(o => F.terms.values.includes(o.invoice?.terms || o.terms));

    if (F.total) {
      r = r.filter(o => {
        const t = o.total || 0;
        if (F.total.op === "gt") return t > F.total.value;
        if (F.total.op === "lt") return t < F.total.value;
        if (F.total.op === "between") return t >= F.total.min && t <= F.total.max;
        return true;
      });
    }
    if (F.date) {
      r = r.filter(o => {
        const d = new Date(o.created_at).getTime();
        if (F.date.op === "last7")  return d >= Date.now() - 7 * 86400000;
        if (F.date.op === "last30") return d >= Date.now() - 30 * 86400000;
        if (F.date.op === "before") return d < new Date(F.date.value).getTime();
        if (F.date.op === "after")  return d > new Date(F.date.value).getTime();
        if (F.date.op === "between") return d >= new Date(F.date.from).getTime() && d <= new Date(F.date.to).getTime() + 86400000;
        return true;
      });
    }
    if (F.overdue?.on != null) {
      const want = F.overdue.on;
      r = r.filter(o => {
        const inv = o.invoice;
        const overdue = !!inv && inv.status !== "paid" && inv.status !== "draft" &&
          (inv.due_date || inv.due_at) && new Date(inv.due_date || inv.due_at).getTime() < Date.now();
        return want ? overdue : !overdue;
      });
    }
    if (F.hasTracking?.on != null) {
      const want = F.hasTracking.on;
      r = r.filter(o => want ? !!o.shipment?.tracking : !o.shipment?.tracking);
    }
    if (F.financed?.on != null) {
      const want = F.financed.on;
      r = r.filter(o => want ? o.invoice?.payment_intent === "extend" : o.invoice?.payment_intent !== "extend");
    }

    return r;
  }, [staged, view, search, filters]);

  const PER_PAGE = 12;
  const totalPages = Math.ceil(viewFiltered.length / PER_PAGE);
  const pageData = viewFiltered.slice((page - 1) * PER_PAGE, page * PER_PAGE);

  // Iter 157 · Phase 2 — Auto-context-switch helper. When we're in Hub mode
  // (no active supplier) and the buyer clicks an action on a cross-supplier
  // row, we transparently switch the active supplier first so every
  // downstream handler (pay, track, drawer, claims) runs in the right
  // scope without the buyer having to manually pick a supplier. Returns
  // the connection if we switched, null if not needed / not found.
  const ensureSupplierContext = (sellerId) => {
    if (!sellerId) return null;
    if (activeSellerId === sellerId) return null;
    const conn = connectionsBySellerId.get(sellerId);
    if (!conn) return null;
    selectSupplier(conn);
    toast.success(`Switched to ${conn.tenant_name || "supplier"}`, { duration: 2500 });
    return conn;
  };

  // Actions
  const pay = (invId, mode = "now", sellerId = null) => {
    // Iter 157 · auto-switch supplier before deep-linking into Payments
    ensureSupplierContext(sellerId);
    // Redirect to Payments portal — query before hash so loc.search picks it up
    nav(`/buyer/payments?pay=${invId}&mode=${mode}#wallet`);
  };
  const reorder = async (oid) => { await api.post(`/orders/${oid}/reorder`); toast.success("Reorder placed"); load(); };
  const insure = async (id) => {
    try { const { data } = await api.post("/insurance/request", { invoice_id: id }); toast.success(`Insured · premium ${money(data.premium)}`); load(); }
    catch { toast.error("Could not insure invoice"); }
  };
  const openOrder = async (oid) => {
    // Iter 157 · Auto-switch supplier context when opening from a Hub-mode
    // row so the drawer + every action button inside it run scoped to the
    // right supplier.
    const { data } = await api.get(`/orders/${oid}`);
    if (!activeSellerId && data?.order?.seller_id) {
      ensureSupplierContext(data.order.seller_id);
    }
    setDrawer(data);
  };

  // ── Open the "Change shipping method" modal seeded with current mode ──
  const openChangeShipping = () => {
    const cur = drawer?.order?.shipping_mode || "traqo";
    setChangeShipMode(cur);
    setChangeShipWaiver(cur === "buyer_arranged");
    setChangeShipOpen(true);
  };
  const submitChangeShipping = async () => {
    const oid = drawer?.order?.id;
    if (!oid) return;
    if (changeShipMode === "buyer_arranged" && !changeShipWaiver) {
      toast.error("Please accept the liability waiver to continue.");
      return;
    }
    setChangeShipSaving(true);
    try {
      const body = { shipping_mode: changeShipMode };
      if (changeShipMode === "buyer_arranged") {
        body.shipping_waiver = { accepted: true };
      }
      await api.patch(`/orders/${oid}/shipping-mode`, body);
      toast.success("Shipping method updated.");
      setChangeShipOpen(false);
      // Refresh the drawer so the new mode + history are visible.
      const { data } = await api.get(`/orders/${oid}`);
      setDrawer(data);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not change shipping method.");
    } finally { setChangeShipSaving(false); }
  };

  // ── Buyer cancel: preview restocking fee → confirm → cancel ───────────
  const openCancel = async () => {
    const oid = drawer?.order?.id;
    if (!oid) return;
    setCancelReason("");
    setCancelPreview(null);
    setCancelOpen(true);
    try {
      const { data } = await api.get(`/orders/${oid}/cancel-preview`);
      setCancelPreview(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not load cancellation preview.");
      setCancelOpen(false);
    }
  };
  const submitCancel = async () => {
    const oid = drawer?.order?.id;
    if (!oid) return;
    setCancelSaving(true);
    try {
      const body = { reason: cancelReason || "buyer_cancelled" };
      // When a fee applies, the buyer is acknowledging it by clicking Confirm.
      if (cancelPreview?.restocking_fee > 0) body.acknowledge_fee = true;
      await api.post(`/orders/${oid}/buyer-cancel`, body);
      toast.success(
        cancelPreview?.restocking_fee > 0
          ? `Order cancelled. Refund of $${cancelPreview.refund_amount.toFixed(2)} is pending.`
          : "Order cancelled."
      );
      setCancelOpen(false);
      setDrawer(null);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail?.message || e?.response?.data?.detail || "Could not cancel order.");
    } finally { setCancelSaving(false); }
  };

  // ── Prebook actions ──────────────────────────────────────────────────
  const refreshDrawer = async (oid) => {
    try {
      const { data } = await api.get(`/orders/${oid}`);
      setDrawer(data);
    } catch { /* best-effort */ }
  };
  const acceptPrebook = async (oid) => {
    setPrebookBusy(true);
    try {
      await api.post(`/orders/${oid}/prebook/accept`);
      toast.success("Deposit paid · order is now active");
      await refreshDrawer(oid);
      load();
    } catch (e) {
      const detail = e?.response?.data?.detail;
      toast.error(detail?.message || detail || "Could not pay deposit.");
    } finally { setPrebookBusy(false); }
  };
  const declinePrebook = async (oid) => {
    if (!window.confirm("Decline this prebook offer? The order will be cancelled.")) return;
    setPrebookBusy(true);
    try {
      await api.post(`/orders/${oid}/prebook/decline`, { reason: "buyer_declined" });
      toast.success("Prebook offer declined");
      setDrawer(null);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not decline.");
    } finally { setPrebookBusy(false); }
  };
  const cancelOverduePrebook = async (oid) => {
    if (!window.confirm("Cancel this overdue prebook? Your deposit will be refunded instantly.")) return;
    setPrebookBusy(true);
    try {
      const { data } = await api.post(`/orders/${oid}/prebook/cancel`, {});
      if (data.refund_status === "completed") {
        toast.success(`Refund of $${(data.refund_amount || 0).toFixed(2)} processed`);
      } else {
        toast.info("Cancelled — seller's wallet is short, refund is pending. We'll auto-retry over 30 days.");
      }
      setDrawer(null);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not cancel.");
    } finally { setPrebookBusy(false); }
  };
  const decideExtension = async (oid, requestId, accept) => {
    setPrebookBusy(true);
    try {
      await api.post(`/orders/${oid}/prebook/decide-extension`, { request_id: requestId, accept });
      toast.success(accept ? "Extension accepted" : "Extension rejected — you can cancel after the original promised date");
      await refreshDrawer(oid);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not respond.");
    } finally { setPrebookBusy(false); }
  };

  // PO upload — real file upload to verqo AI via /orders/po-upload.
  // Spreadsheets / CSVs are parsed locally on the server (sub-second);
  // PDFs and images go through verqo AI vision (~3-6 s).
  const ACCEPTED_EXT = ["pdf","png","jpg","jpeg","heic","heif","webp","csv","txt","xlsx","xls","docx","doc"];
  const MAX_BYTES = 25 * 1024 * 1024;
  const FAST_EXT = new Set(["csv","txt","xlsx","xls"]);

  const parsingLabelFor = (f) => {
    const ext = (f?.name?.split(".").pop() || "").toLowerCase();
    if (FAST_EXT.has(ext)) return "Reading spreadsheet…";
    if (ext === "pdf") return "Scanning PDF…";
    if (["png","jpg","jpeg","heic","heif","webp"].includes(ext)) return "Reading image…";
    if (["doc","docx"].includes(ext)) return "Reading document…";
    return "Parsing PO…";
  };

  const pickPoFile = (f) => {
    if (!f) return;
    const ext = (f.name.split(".").pop() || "").toLowerCase();
    if (!ACCEPTED_EXT.includes(ext)) {
      toast.error(`Unsupported file type (.${ext}). Accepted: PDF, image, CSV, XLSX, DOC, TXT.`);
      return;
    }
    if (f.size > MAX_BYTES) { toast.error("File too large (max 25 MB)"); return; }
    if (f.size === 0) { toast.error("Empty file"); return; }
    setPoFile(f);
  };

  // Sample CSV download — purely a UX guarantee; verqo AI parses your existing
  // supplier POs without it, but a starter template helps first-time buyers.
  const downloadSampleCSV = () => {
    const csv = [
      "Brand,Product,SKU,Qty,Unit Price",
      "GlowCo,Hydration Serum 30ml,HYDRA-30,12,18.00",
      "GlowCo,Sun Shield SPF 50,SHIELD-50,6,22.50",
      "GlowCo,Night Repair Cream,REPAIR-N,4,32.00",
    ].join("\n") + "\n";
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "verqo-sample-po.csv";
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const parsePO = async () => {
    if (!poFile) return;
    if (!activeSellerId) {
      toast.error("Pick a supplier first — PO uploads are addressed to one supplier at a time.");
      return;
    }
    setPoParsing(true);
    try {
      const fd = new FormData();
      fd.append("file", poFile);
      fd.append("seller_id", activeSellerId);
      const { data } = await api.post("/orders/po-upload", fd, {
        headers: { "Content-Type": "multipart/form-data" },
        timeout: 45_000, // local parse ~1s, verqo AI ~3-8s; 45s is plenty
      });
      if (!data.items?.length) {
        toast.error(data.error || "No line items could be extracted from this file.");
        setPoParsing(false);
        return;
      }
      // Unit price becomes editable for unmatched rows (user can correct OCR).
      data.items = data.items.map(it => ({
        ...it,
        unit_price: it.unit_price ?? "",
      }));
      setPoResult(data);
    } catch (e) {
      const msg = e?.response?.data?.detail || e?.response?.data?.error || "Parse failed. Try a different file.";
      toast.error(typeof msg === "string" ? msg : "Parse failed");
    }
    finally { setPoParsing(false); }
  };

  // Edit a single PO line — used for inline price correction on unmatched rows.
  const editPoItem = (idx, patch) => {
    setPoResult(prev => {
      const items = prev.items.map((it, i) => i === idx ? { ...it, ...patch } : it);
      return { ...prev, items };
    });
  };

  const confirmPO = async () => {
    if (!poResult || !activeSellerId) return;
    // Every line routes to the active storefront supplier. Matched lines carry
    // their catalogue product_id; unmatched lines go through with product_id=null
    // (seller receives a "free-form" line with the buyer's typed name + price).
    const resolved = poResult.items.map(it => {
      const price = Number(it.unit_price);
      const unit_price = Number.isFinite(price) && price > 0 ? price : null;
      const line_total = unit_price != null ? Number((unit_price * it.qty).toFixed(2)) : null;
      return { ...it, seller_id: activeSellerId, unit_price, line_total };
    });
    const invalid = resolved.filter(it => it.line_total == null);
    if (invalid.length) {
      toast.error(`${invalid.length} item${invalid.length>1?'s':''} missing a unit price. Fill them in to continue.`);
      return;
    }
    setPoSubmitting(true);
    try {
      // Build the shipping-waiver payload only when buyer-arranged is selected.
      // The exact ack text is mirrored on the backend so the saved row + the
      // UI checkbox can never drift apart.
      const waiver = poShippingMode === "buyer_arranged" ? {
        accepted: poWaiverAccepted,
        ack_text: "I am arranging my own shipping. verqo and the seller are not responsible for loss, damage, or delay once the goods leave the seller's warehouse.",
        user_agent: typeof navigator !== "undefined" ? navigator.userAgent : null,
      } : null;
      const payload = {
        items: resolved.map(it => ({
          product_id: it.product_id || null,
          product:    it.product,
          sku:        it.sku,
          qty:        it.qty,
          unit_price: it.unit_price,
          line_total: it.line_total,
          seller_id:  it.seller_id,
          brand:      it.brand,
        })),
        po_number: poResult.po_number,
        terms:     "net30",
        shipping_mode: poShippingMode,
        shipping_waiver: waiver,
      };
      const { data } = await api.post("/orders/po-confirm", payload);
      const n = (data.orders || []).length;
      toast.success(`${n} order${n>1?'s':''} sent to ${activeSellerName || "supplier"} · awaiting confirmation`);
      setPoOpen(false); setPoResult(null); setPoFile(null);
      await load();
      nav("/buyer/orders#all");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not send this PO to the supplier");
    }
    finally { setPoSubmitting(false); }
  };

  // Journey view sections
  const journeyOrders = (stage) => staged.filter(o => o.stage === stage);
  const scrollToStep = (n) => {
    const el = document.getElementById(`step-${n}`);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  return (
    <>
      {/* Top header — context + actions */}
      <div className="border-b border-border bg-background/80 backdrop-blur sticky top-0 z-20 px-4 sm:px-6 lg:px-8 py-3 flex items-center justify-between gap-4">
        <div>
          <div className="text-[10px] mono uppercase tracking-[0.2em] text-foreground/50">Commerce · Orders</div>
          <h1 className="display text-2xl mt-1 tracking-tight">{VIEW_LABEL[view]}</h1>
        </div>
        <div className="flex gap-2">
          {/* PO upload is only meaningful inside a supplier storefront — the PO
              is addressed TO that specific supplier. In Hub mode, prompt the
              buyer to pick a supplier first. */}
          {!inHubMode ? (
            <Button variant="outline" onClick={()=>setPoOpen(true)} data-testid="po-upload-btn" className="rounded-full h-10 px-4">
              <FileUp className="w-4 h-4 mr-1.5"/>Upload PO
            </Button>
          ) : (
            <Button
              variant="outline"
              onClick={() => toast.info("Pick a supplier first — PO uploads are addressed to one supplier at a time.")}
              data-testid="po-upload-btn-hub"
              className="rounded-full h-10 px-4 opacity-70"
              title="Switch to a supplier storefront to upload a PO"
            >
              <FileUp className="w-4 h-4 mr-1.5"/>Upload PO
            </Button>
          )}
          <Button
            onClick={() => {
              // Always land on the Journey "Place Order" step. StepPlace handles
              // the conditional Browse-catalogue card based on `hasCatalog`, so a
              // buyer without the Catalog app sees only the Upload PO option.
              setActiveStep("place");
              setStepInited(true); // freeze auto-detect — user explicitly chose
              nav("/buyer/orders#journey");
            }}
            data-testid="new-order-btn"
            className="rounded-full bg-foreground text-background hover:bg-foreground/90 h-10 px-4"
          >
            <ShoppingBag className="w-4 h-4 mr-1.5"/>New order
          </Button>
        </div>
      </div>

      {/* Sticky pill-tab filter bar — mirrors the journey stages 1:1 */}
      <div className="sticky top-[63px] z-10 bg-background/85 backdrop-blur border-b border-border px-4 sm:px-6 lg:px-8 py-3" data-testid="orders-tabbar">
        <div className="flex items-center gap-1.5 overflow-x-auto scrollbar-thin -mx-1 px-1">
          {VIEW_TABS.map(tab => {
            const ct = tab.countKey ? (counts[tab.countKey] || 0) : null;
            const active = view === tab.key;
            const toneRing = {
              amber:   "ring-amber-200 text-amber-800 bg-amber-50",
              rose:    "ring-rose-200 text-rose-800 bg-rose-50",
              blue:    "ring-blue-200 text-blue-800 bg-blue-50",
              indigo:  "ring-indigo-200 text-indigo-800 bg-indigo-50",
              emerald: "ring-emerald-200 text-emerald-800 bg-emerald-50",
            }[tab.tone] || "ring-foreground/15 bg-secondary";
            return (
              <button
                key={tab.key}
                onClick={() => nav(`/buyer/orders#${tab.key}`)}
                data-testid={`tab-${tab.key}`}
                className={`relative shrink-0 inline-flex items-center gap-2 rounded-full text-sm pl-3.5 pr-2 py-1.5 border transition-all ${
                  active
                    ? "bg-foreground text-background border-foreground"
                    : "bg-white text-foreground/75 border-border hover:border-foreground/30 hover:text-foreground"
                }`}
              >
                <tab.icon className="w-3.5 h-3.5"/>
                <span className="font-medium">{tab.label}</span>
                {ct != null && (
                  <span className={`text-[10px] mono px-1.5 py-0.5 rounded-full min-w-[18px] text-center ring-1 ${
                    active
                      ? "bg-background/15 text-background ring-transparent"
                      : ct === 0 ? "text-foreground/40 bg-secondary ring-transparent" : toneRing
                  }`}>{ct}</span>
                )}
                {tab.action && !active && ct > 0 && (
                  <span className="absolute -top-1 -right-1 w-2 h-2 rounded-full bg-rose-500 ring-2 ring-background" title="Needs your action" />
                )}
              </button>
            );
          })}
        </div>
      </div>

      <div className="p-8 space-y-6">

        {!activeSellerId && (
          <HubModeNotice
            page="Orders cockpit"
            description={`Showing ${counts.all || 0} order${(counts.all||0)===1?"":"s"} across all ${(connections || []).length} connected supplier${(connections||[]).length===1?"":"s"}. Click any action — Pay, Book carrier, Reorder — and we'll auto-switch to that supplier for you.`}
          />
        )}

        {/* Iter 157 · Phase 3 — Supplier quick-filter pills (Hub mode only).
            Lets the buyer instantly group + scope to a single supplier with
            one click. The "All" pill returns them to the cross-supplier
            cockpit view. */}
        {!activeSellerId && (connections || []).length > 1 && view !== "journey" && view !== "claims" && (
          <SupplierFilterPills
            connections={connections}
            orders={orders}
            onPick={(conn) => { selectSupplier(conn); toast.success(`Scoped to ${conn.tenant_name}`, { duration: 2000 }); }}
          />
        )}

        {view === "claims" && (
          <ClaimsTab role="buyer" orders={orders} />
        )}

        {view === "journey" && (
          <JourneyFocusMode
            activeStep={activeStep}
            setActiveStep={setActiveStep}
            counts={counts}
            allSteps={ALL_STEPS}
            hasCatalog={hasCatalog}
            invoices={invoices}
            journeyOrders={journeyOrders}
            openOrder={openOrder}
            pay={pay}
            nav={nav}
            setPoOpen={setPoOpen}
            activeSellerName={activeSellerName}
          />
        )}

        {view !== "journey" && view !== "claims" && (
          /* Table-style list view (used for All / Action needed / In progress / Delivered) */
          <>
            {/* Quick stat row — gives context per view */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid={`view-${view}-stats`}>
              {(() => {
                const tiles = {
                  all: [
                    { l: "Total orders", v: counts.all },
                    { l: "Action needed", v: counts.action },
                    { l: "In progress", v: counts.progress },
                    { l: "Delivered", v: counts.delivered },
                  ],
                  action: [
                    { l: "Action needed", v: counts.action },
                    { l: "Total due", v: money(staged.filter(o=>o.stage==="pay").reduce((s,o)=>s+(o.invoice?.amount||0),0)), big: true },
                    { l: "Awaiting pickup", v: counts.pickup },
                    { l: "Invoices to pay", v: counts.pay },
                  ],
                  progress: [
                    { l: "In progress", v: counts.progress },
                    { l: "Confirming", v: counts.seller_confirm },
                    { l: "Preparing", v: counts.pack },
                    { l: "In transit", v: counts.shipped },
                  ],
                  done: [
                    { l: "Delivered", v: counts.delivered },
                    { l: "Total spent", v: money(staged.filter(o=>o.stage==="delivered").reduce((s,o)=>s+o.total,0)), big: true },
                    { l: "Reorder rate", v: "62%" },
                    { l: "On-time rate", v: "97%" },
                  ],
                };
                return (tiles[view] || tiles.all).map((t, i) => (
                  <div key={i} className="rounded-2xl border border-border bg-white p-5">
                    <div className="text-[10px] mono uppercase tracking-[0.15em] text-foreground/50">{t.l}</div>
                    <div className={`display ${t.big ? "text-3xl" : "text-2xl"} mt-2 tracking-tight`}>{t.v}</div>
                    {t.hint && <div className="text-[11px] text-foreground/50 mt-1">{t.hint}</div>}
                  </div>
                ));
              })()}
            </div>

            {/* Composable search + filter (Shopify-style) */}
            <SearchAndFilter
              page="buyer_orders"
              search={search}
              onSearchChange={setSearch}
              filters={filters}
              onChange={setFilters}
              totalCount={viewFiltered.length}
              facets={[
                {
                  id: "status", label: "Status", icon: Clock, type: "multi",
                  options: [
                    { value: "seller_confirm",    label: "Awaiting confirmation" },
                    { value: "pay",               label: "To pay" },
                    { value: "pack",              label: "Preparing" },
                    { value: "ready_for_pickup",  label: "Ready for pickup" },
                    { value: "shipped",           label: "In transit" },
                    { value: "delivered",         label: "Delivered" },
                  ],
                },
                {
                  id: "supplier", label: "Supplier", icon: Store, type: "multi",
                  options: supplierOptions,
                },
                { id: "total", label: "Total",      icon: DollarSign,   type: "numeric", numericLabel: "$" },
                { id: "date",  label: "Order date", icon: CalendarDays, type: "date" },
                {
                  id: "terms", label: "Payment terms", icon: Receipt, type: "multi",
                  options: [
                    { value: "net14", label: "Net-14" },
                    { value: "net30", label: "Net-30" },
                    { value: "net60", label: "Net-60" },
                    { value: "net90", label: "Net-90" },
                    { value: "prepaid", label: "Prepay only" },
                  ],
                },
                { id: "overdue",     label: "Overdue invoice", icon: AlertTriangle, type: "boolean" },
                { id: "hasTracking", label: "Has tracking",    icon: MapPin,        type: "boolean" },
                { id: "financed",    label: "Financed",        icon: Sparkles,      type: "boolean" },
              ]}
            />

            {/* Table — lean layout (Feb 2026): per-tab column gating, Documents
               cell merges Invoice + Pack list + Tracking, Est delivery → Age
               in stage (or Delivered date on Delivered tab), View button
               removed (row click opens drawer), explicit verb on every action
               button + read-only chip for awaiting stages. */}
            <div className="rounded-2xl bg-white border border-border overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                <thead className="bg-secondary/50 text-left text-xs uppercase tracking-widest text-foreground/60">
                  <tr>
                    <th className="px-5 py-3 sticky left-0 bg-secondary/50">Order</th>
                    <th className="px-5 py-3">Brand</th>
                    <th className="px-5 py-3">Items</th>
                    <th className="px-5 py-3">Total</th>
                    <th className="px-5 py-3">Status</th>
                    {/* Documents column hidden on the To-pay tab — invoice
                       chip shows on the action button itself instead. */}
                    {view !== "action" && <th className="px-5 py-3">Documents</th>}
                    <th className="px-5 py-3">{view === "done" ? "Delivered" : "Age"}</th>
                    <th className="px-5 py-3"/>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border">
                  {pageData.map(o => {
                    const meta = STAGE_META[o.stage] || STAGE_META.delivered;
                    const skuCount = (o.items || []).length;
                    const totalUnits = (o.items || []).reduce((s,i)=>s+(i.qty||0), 0);
                    const slaTone = ageSlaTone(o, view);
                    const slaBorder = slaTone === "rose"
                      ? "border-l-2 border-l-rose-500"
                      : slaTone === "amber"
                        ? "border-l-2 border-l-amber-400"
                        : "border-l-2 border-l-transparent";
                    return (
                      <tr key={o.id} className={`hover:bg-secondary/30 cursor-pointer ${slaBorder}`} data-testid={`row-${o.code}`} onClick={()=>openOrder(o.id)}>
                        <td className="px-5 py-4 mono text-foreground/70 sticky left-0 bg-white">{o.code}</td>
                        <td className="px-5 py-4">{o.seller_name}</td>
                        <td className="px-5 py-4">
                          <div className="text-sm whitespace-nowrap">
                            {skuCount} SKU{skuCount === 1 ? "" : "s"}
                            <span className="text-foreground/40 mx-1.5">·</span>
                            <span className="text-foreground/65">{totalUnits} units</span>
                          </div>
                        </td>
                        <td className="px-5 py-4 display">{money(o.total)}</td>
                        <td className="px-5 py-4"><span className={`chip ${meta.chip}`}>{meta.emoji} {meta.label}</span></td>
                        {view !== "action" && (
                          <td className="px-5 py-4" onClick={e => e.stopPropagation()}>
                            <BuyerDocumentsCell order={o}/>
                          </td>
                        )}
                        <td className="px-5 py-4 text-xs text-foreground/60" data-testid={`age-${o.code}`}>
                          {view === "done"
                            ? (o.shipment?.delivered_at
                                ? new Date(o.shipment.delivered_at).toLocaleDateString()
                                : (o.est_delivery ? new Date(o.est_delivery).toLocaleDateString() : "✓"))
                            : (
                              <span className={
                                slaTone === "rose"  ? "mono text-rose-700 font-medium" :
                                slaTone === "amber" ? "mono text-amber-700 font-medium" :
                                "mono"
                              }>
                                {ageInStageLabel(o)}
                                {slaTone && <span className="ml-1 text-[9px] uppercase tracking-widest">SLA</span>}
                              </span>
                            )}
                        </td>
                        <td className="px-5 py-4 text-right space-x-1" onClick={e => e.stopPropagation()}>
                          {/* Read-only stages render the same italic chip in both modes. */}
                          {o.stage === "seller_confirm" && (
                            <span className="text-[10px] mono italic text-foreground/50 inline-flex items-center gap-1" data-testid={`buyer-await-confirm-${o.code}`}>
                              <Clock className="w-3 h-3"/>Awaiting supplier
                            </span>
                          )}
                          {o.stage === "pack" && (
                            <span className="text-[10px] mono italic text-foreground/50 inline-flex items-center gap-1" data-testid={`buyer-await-pack-${o.code}`}>
                              <Package className="w-3 h-3"/>Seller is packing
                            </span>
                          )}
                          {/* Track shipment is read-only-ish (just opens shipping page) — same in both modes. */}
                          {o.stage === "shipped" && (
                            <Button size="sm" variant="outline" onClick={()=>nav("/buyer/shipping")} data-testid={`track-${o.code}`} className="rounded-full">Track shipment</Button>
                          )}
                          {/* Action stages — Storefront mode acts inline, Hub mode auto-switches first.
                             Iter 157 · Phase 2 — every action button is direct (not "Open in Acme" → then Pay).
                             ensureSupplierContext() runs transparently when the buyer clicks. */}
                          {!inHubMode && o.stage === "pay" && (
                            <Button size="sm" onClick={()=>pay(o.invoice.id, "now", o.seller_id)} data-testid={`pay-${o.code}`} className="rounded-full bg-foreground text-background hover:bg-foreground/90">
                              <CreditCard className="w-3 h-3 mr-1"/>Pay {money(o.invoice?.amount || 0)}
                            </Button>
                          )}
                          {!inHubMode && o.stage === "ready_for_pickup" && (
                            <Button size="sm" onClick={()=>{ ensureSupplierContext(o.seller_id); nav("/buyer/shipping"); }} data-testid={`book-${o.code}`} className="rounded-full bg-foreground text-background hover:bg-foreground/90"><Truck className="w-3 h-3 mr-1"/>Book carrier</Button>
                          )}
                          {!inHubMode && o.stage === "delivered" && (
                            <Button size="sm" onClick={()=>{ ensureSupplierContext(o.seller_id); reorder(o.id); }} data-testid={`reorder-${o.code}`} variant="outline" className="rounded-full"><Repeat className="w-3 h-3 mr-1"/>Reorder</Button>
                          )}
                          {/* Hub-mode row · direct action with transparent context-switch.
                             Disconnected suppliers (rare — stale data) fall back to a clear chip. */}
                          {inHubMode && ["pay", "ready_for_pickup", "delivered"].includes(o.stage) && (
                            connectionsBySellerId.has(o.seller_id) ? (
                              o.stage === "pay" ? (
                                <Button
                                  size="sm"
                                  onClick={() => pay(o.invoice.id, "now", o.seller_id)}
                                  data-testid={`hub-pay-${o.code}`}
                                  className="rounded-full bg-foreground text-background hover:bg-foreground/90"
                                  title={`Switches to ${o.seller_name} and opens the Pay drawer`}
                                >
                                  <CreditCard className="w-3 h-3 mr-1"/>Pay {money(o.invoice?.amount || 0)}
                                </Button>
                              ) : o.stage === "ready_for_pickup" ? (
                                <Button
                                  size="sm"
                                  onClick={() => { ensureSupplierContext(o.seller_id); nav("/buyer/shipping"); }}
                                  data-testid={`hub-book-${o.code}`}
                                  className="rounded-full bg-foreground text-background hover:bg-foreground/90"
                                  title={`Switches to ${o.seller_name} and opens Shipping`}
                                >
                                  <Truck className="w-3 h-3 mr-1"/>Book carrier
                                </Button>
                              ) : (
                                <Button
                                  size="sm"
                                  variant="outline"
                                  onClick={() => { ensureSupplierContext(o.seller_id); reorder(o.id); }}
                                  data-testid={`hub-reorder-${o.code}`}
                                  className="rounded-full"
                                  title={`Switches to ${o.seller_name} and reorders`}
                                >
                                  <Repeat className="w-3 h-3 mr-1"/>Reorder
                                </Button>
                              )
                            ) : (
                              <span className="text-[10px] mono italic text-foreground/40 inline-flex items-center gap-1" data-testid={`disconnected-${o.code}`}>
                                <Clock className="w-3 h-3"/>Disconnected · view-only
                              </span>
                            )
                          )}
                        </td>
                      </tr>
                    );
                  })}
                  {pageData.length === 0 && (
                    <tr>
                      <td colSpan={view === "action" ? 7 : 8} className="p-0" data-testid="buyer-orders-empty">
                        {/* Sample preview — renders ONLY after the first fetch
                         *  completes AND the buyer has TRULY zero orders. This
                         *  prevents the 3-sample flash on accounts (like the
                         *  demo seed) that load real orders ~3s later.
                         *  Click any row → opens sample order drawer. */}
                        {!initialLoaded
                          ? (
                            <div className="py-16 text-center text-foreground/40 text-xs" data-testid="buyer-orders-loading">
                              <div className="inline-block w-5 h-5 rounded-full border-2 border-foreground/15 border-t-foreground/50 animate-spin" />
                              <div className="mt-3">Loading your orders…</div>
                            </div>
                          )
                          : orders.length === 0 && view === "all"
                          ? <SampleOrdersPreviewBuyer />
                          : (
                            <div className="text-center py-16 text-foreground/55 text-sm">
                              <div className="display text-base">Nothing here yet</div>
                              <div className="text-xs mt-1 text-foreground/50">
                                {view === "action"   && "Nothing waiting on you right now — all caught up. 🎉"}
                                {view === "progress" && "Nothing in flight right now. Confirmed, packing, and in-transit orders all show up here together."}
                                {view === "done"     && "Your delivered orders will appear here."}
                                {view === "all"      && "Place your first order to get started."}
                              </div>
                            </div>
                          )}
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
              </div>

              {totalPages > 1 && (
                <div className="px-5 py-3 border-t border-border flex items-center justify-between text-xs">
                  <span className="text-foreground/60">Page {page} of {totalPages}</span>
                  <div className="flex gap-1">
                    <button onClick={()=>setPage(p=>Math.max(1,p-1))} disabled={page===1} className="px-3 py-1 rounded-md border border-border disabled:opacity-30 hover:bg-secondary">Back</button>
                    {Array.from({length: totalPages}, (_, i) => (
                      <button key={i} onClick={()=>setPage(i+1)} className={`px-3 py-1 rounded-md ${page===i+1?"bg-foreground text-background":"hover:bg-secondary"}`}>{i+1}</button>
                    ))}
                    <button onClick={()=>setPage(p=>Math.min(totalPages,p+1))} disabled={page===totalPages} className="px-3 py-1 rounded-md border border-border disabled:opacity-30 hover:bg-secondary">Next</button>
                  </div>
                </div>
              )}
            </div>
          </>
        )}
      </div>

      {/* Order detail drawer */}
      <Sheet open={!!drawer} onOpenChange={(v)=>!v && setDrawer(null)}>
        <SheetContent className="w-full sm:max-w-xl overflow-y-auto">
          {drawer && (() => {
            const o = drawer.order;
            const inv = drawer.invoice;
            const stage = getStage(o, inv);
            const meta = STAGE_META[stage] || STAGE_META.delivered;
            const StageIcon = meta.icon;
            const banner = inHubMode
              ? null  // Hub mode shows the supplier-redirect banner above instead.
              : stage === "pay" ? { title: "Invoice ready — pay now to start packing (Step 5)", cta: "Pay invoice", action: () => { pay(inv.id); setDrawer(null); }, color: "rose" }
              : stage === "ready_for_pickup" ? { title: "Order is packed — book a carrier to schedule pickup", cta: "Book carrier", action: () => { nav("/buyer/shipping"); setDrawer(null); }, color: "amber" }
              : stage === "shipped" ? { title: "Shipment in transit — track below", cta: "View tracking", action: () => nav("/buyer/shipping"), color: "indigo" }
              : stage === "seller_confirm" ? { title: "Awaiting seller confirmation", cta: null, color: "amber" }
              : null;
            return <>
              <SheetHeader>
                <SheetTitle className="display flex items-center justify-between gap-2">
                  <span>Order {o.code}</span>
                  <span className={`chip ${meta.chip}`}>{meta.emoji} {meta.label}</span>
                </SheetTitle>
              </SheetHeader>

              {/* Hub-mode banner — buyer is monitoring across all suppliers.
                 Surfaces a single prominent CTA at the top of the drawer that
                 switches the active-supplier context and routes them into
                 that supplier's storefront where the actual action lives. */}
              {inHubMode && ["pay", "ready_for_pickup", "delivered"].includes(stage) && (
                <div className="mt-4 rounded-xl bg-violet-50 border border-violet-200 p-4 flex items-center gap-3" data-testid="buyer-drawer-hub-cta">
                  <div className="w-9 h-9 rounded-full bg-violet-100 grid place-items-center shrink-0">
                    <ArrowRight className="w-4 h-4 text-violet-700"/>
                  </div>
                  <div className="flex-1 text-sm">
                    <div className="font-medium text-violet-900">Take action in {o.seller_name}</div>
                    <div className="text-xs text-violet-700/80 mt-0.5">Hub view is monitor-only. Switch into the supplier's storefront to {stage === "pay" ? "pay this invoice" : stage === "ready_for_pickup" ? "book a carrier" : "reorder or file a claim"}.</div>
                  </div>
                  <Button
                    size="sm"
                    onClick={() => openInStorefront(o)}
                    data-testid="buyer-drawer-open-storefront"
                    className="rounded-full bg-foreground text-background hover:bg-foreground/90"
                  >
                    Open storefront <ArrowRight className="w-3 h-3 ml-1"/>
                  </Button>
                </div>
              )}

              {banner && (
                <div className={`mt-4 rounded-xl p-4 flex items-center gap-3 ${banner.color === "rose" ? "bg-rose-50 border border-rose-200" : banner.color === "indigo" ? "bg-indigo-50 border border-indigo-200" : "bg-amber-50 border border-amber-200"}`}>
                  <StageIcon className={`w-5 h-5 shrink-0 ${banner.color === "rose" ? "text-rose-700" : banner.color === "indigo" ? "text-indigo-700" : "text-amber-700"}`}/>
                  <div className="flex-1 text-sm">{banner.title}</div>
                  {banner.cta && <Button size="sm" onClick={banner.action} className="rounded-full bg-foreground text-background hover:bg-foreground/90">{banner.cta}</Button>}
                </div>
              )}

              <div className="mt-5 space-y-5">
                <div className="rounded-xl bg-secondary/50 p-4 flex justify-between items-end">
                  <div>
                    <div className="text-xs uppercase tracking-widest text-foreground/60">Order total</div>
                    <div className="display text-3xl mt-1">{money(o.total)}</div>
                    <div className="text-xs text-foreground/60 mt-1">{o.terms.toUpperCase()} · {o.seller_name}</div>
                  </div>
                  <div className="flex flex-col gap-1.5">
                    <Button
                      size="sm" variant="outline" className="rounded-full"
                      data-testid="buyer-order-download-invoice"
                      onClick={async () => {
                        // Resolve the invoice id — prefer the one already
                        // joined in via invoiceFor() during render, otherwise
                        // hit /invoices?order_id=… on demand so older orders
                        // (whose invoice fell out of the cached page) still
                        // download cleanly.
                        let invId = o.invoice?.id;
                        let invCode = o.invoice?.code;
                        if (!invId) {
                          try {
                            const { data } = await api.get(`/invoices`, { params: { order_id: o.id } });
                            const inv = (data || [])[0];
                            if (inv) { invId = inv.id; invCode = inv.code; }
                          } catch { /* fall through to the toast below */ }
                        }
                        if (!invId) { toast.info("Invoice not generated yet"); return; }
                        try {
                          const res = await api.get(`/invoices/${invId}/pdf`, { responseType: "blob" });
                          const url = URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
                          const a = document.createElement("a");
                          a.href = url;
                          a.download = `invoice-${invCode || invId.slice(0, 8)}.pdf`;
                          a.click();
                          URL.revokeObjectURL(url);
                        } catch (e) {
                          toast.error(e.response?.data?.detail || "Invoice download failed");
                        }
                      }}
                    ><Download className="w-3.5 h-3.5 mr-1"/>Invoice</Button>
                    {/* Pack list — only after seller marks the order shipped.
                        Gated to status `transit` or `delivered` so buyers don't
                        download a half-empty doc before goods leave the dock. */}
                    {(o.status === "transit" || o.status === "delivered") && (
                      <Button
                        size="sm" variant="outline" className="rounded-full"
                        data-testid="buyer-order-download-pack-list"
                        onClick={async () => {
                          try {
                            const res = await api.get(`/orders/${o.id}/pack-list/pdf`, { responseType: "blob" });
                            const url = URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
                            const a = document.createElement("a");
                            a.href = url;
                            a.download = `pack-list-${o.code || o.id.slice(0, 8)}.pdf`;
                            a.click();
                            URL.revokeObjectURL(url);
                          } catch (e) { toast.error("Pack list download failed"); }
                        }}
                      ><Download className="w-3.5 h-3.5 mr-1"/>Pack list</Button>
                    )}
                    <Button size="sm" onClick={()=>{reorder(o.id); setDrawer(null);}} className="rounded-full bg-foreground text-background hover:bg-foreground/90"><Repeat className="w-3.5 h-3.5 mr-1"/>Reorder</Button>
                    {/* Buyer-initiated cancel — only available until the carrier
                        has picked the order up. After that (status=transit
                        / in_transit / shipped / delivered) it's locked.
                        Status names match what `services/wms.py` actually
                        writes; older orders may still use `in_transit`. */}
                    {!["shipped", "transit", "in_transit", "out_for_delivery",
                       "delivered", "released", "cancelled", "auto_cancelled",
                       "refunded", "paid_post_delivered"].includes(o.status) && (
                      <Button
                        size="sm" variant="outline"
                        onClick={openCancel}
                        data-testid="buyer-cancel-order-btn"
                        className="rounded-full text-red-600 border-red-200 hover:bg-red-50 hover:text-red-700"
                      ><X className="w-3.5 h-3.5 mr-1"/>Cancel order</Button>
                    )}
                  </div>
                </div>

                {/* Timeline */}
                <div>
                  <div className="text-xs uppercase tracking-widest text-foreground/60 mb-3">Fulfilment timeline</div>
                  <div className="space-y-3">
                    {drawer.timeline.map((t, i) => (
                      <div key={i} className="flex gap-3 items-start">
                        {t.done ? <CheckCircle2 className="w-5 h-5 text-foreground mt-0.5"/> : <Circle className="w-5 h-5 text-foreground/30 mt-0.5"/>}
                        <div className="flex-1">
                          <div className="text-sm font-medium">{t.label}</div>
                          <div className="text-xs text-foreground/50">{new Date(t.date).toLocaleString()}</div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Items */}
                <div>
                  <div className="text-xs uppercase tracking-widest text-foreground/60 mb-3">Order items</div>
                  <div className="rounded-xl border border-border divide-y divide-border">
                    {(o.items || []).map((it, i) => (
                      <div key={i} className="flex justify-between p-3 text-sm">
                        <div>
                          <div className="font-medium">{it.name}</div>
                          <div className="text-xs text-foreground/60">{it.qty} units × {money(it.price)}</div>
                        </div>
                        <div className="display">{money(it.qty * it.price)}</div>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Shipping method — buyer can change it any time before the
                    order is marked shipped/released. Default policy is that
                    shipping belongs to the buyer (B2B norm); "Seller arranges"
                    is only shown when the seller has explicitly opted in. */}
                {/* Prebook (deposit + balance) — surfaces buyer actions
                    depending on order status. Three views: offered (accept /
                    decline), active (timeline + cancel-if-overdue + decide
                    pending extension), cancelled (refund status). */}
                {(o.status === "prebook_offered" || o.status === "prebook_active" ||
                  (o.status === "cancelled" && o.prebook)) && (() => {
                  const pb = o.prebook || {};
                  const promised = pb.promised_ready_by ? new Date(pb.promised_ready_by) : null;
                  const isOverdue = o.status === "prebook_active" && promised && promised.getTime() < Date.now();
                  const pending = (pb.extension_requests || []).filter(r => r.status === "pending");
                  return (
                    <div data-testid="buyer-drawer-prebook">
                      <div className="text-xs uppercase tracking-widest text-foreground/60 mb-3">Prebook</div>

                      {/* Status pill */}
                      <div className="mb-3 inline-flex items-center gap-1.5 text-[10.5px] mono uppercase tracking-widest">
                        {o.status === "prebook_offered" && (<span className="px-2 py-0.5 rounded-full bg-amber-100 text-amber-900 border border-amber-200">Awaiting your acceptance</span>)}
                        {o.status === "prebook_active" && !isOverdue && (<span className="px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-900 border border-emerald-200">Active · deposit paid</span>)}
                        {o.status === "prebook_active" && isOverdue && (<span className="px-2 py-0.5 rounded-full bg-red-100 text-red-900 border border-red-200">Overdue</span>)}
                        {o.status === "cancelled" && (<span className="px-2 py-0.5 rounded-full bg-foreground/10 text-foreground/80 border border-foreground/15">Cancelled</span>)}
                      </div>

                      {/* Summary card */}
                      <div className="rounded-xl border border-border bg-white p-4 grid grid-cols-3 gap-3 mb-3 text-center">
                        <div><div className="text-[10px] mono uppercase tracking-widest text-foreground/50">Deposit{pb.deposit_paid ? " paid" : " due"}</div><div className={`display text-base mono ${pb.deposit_paid ? "text-emerald-700" : ""}`}>{money(pb.deposit_amount || 0)}</div></div>
                        <div><div className="text-[10px] mono uppercase tracking-widest text-foreground/50">Balance</div><div className="display text-base mono">{money(pb.balance_amount || 0)}</div></div>
                        <div><div className="text-[10px] mono uppercase tracking-widest text-foreground/50">{isOverdue ? "Promised (overdue)" : "Ready by"}</div><div className={`text-[13px] font-medium ${isOverdue ? "text-red-700" : ""}`}>{promised ? promised.toLocaleDateString() : "—"}</div></div>
                      </div>

                      {/* Extensions log */}
                      {Array.isArray(pb.extension_requests) && pb.extension_requests.length > 0 && (
                        <div className="rounded-xl border border-border bg-secondary/30 p-3 mb-3 space-y-1.5" data-testid="buyer-prebook-ext-log">
                          <div className="text-[10.5px] mono uppercase tracking-widest text-foreground/45">Extensions used: {pb.extensions_used || 0} of 3</div>
                          {pb.extension_requests.map((r) => (
                            <div key={r.id} className="text-[11.5px] text-foreground/65 flex items-center gap-2 flex-wrap">
                              <span className={`mono uppercase tracking-widest text-[9.5px] px-1.5 py-0.5 rounded border ${r.status === "accepted" || r.status === "auto_accepted" ? "bg-emerald-50 text-emerald-700 border-emerald-200" : r.status === "rejected" ? "bg-red-50 text-red-700 border-red-200" : "bg-amber-50 text-amber-700 border-amber-200"}`}>{r.status}</span>
                              <span>→ {(r.new_promised_date || "").slice(0, 10)}</span>
                              {r.reason && <span className="text-foreground/45 italic">· {r.reason.slice(0, 80)}</span>}
                            </div>
                          ))}
                        </div>
                      )}

                      {/* Pending extension request → accept / reject */}
                      {pending.length > 0 && (
                        <div className="rounded-xl border border-amber-300 bg-amber-50 p-4 mb-3" data-testid="buyer-prebook-ext-pending">
                          <div className="text-[12.5px] font-medium text-amber-900 mb-1">
                            Seller is requesting an extension ({(pb.extensions_used || 0) + 1} of 3)
                          </div>
                          <div className="text-[12px] text-amber-900/85 mb-1">New ready date: <strong>{(pending[0].new_promised_date || "").slice(0, 10)}</strong></div>
                          {pending[0].reason && <div className="text-[11.5px] text-amber-900/70 italic mb-2">"{pending[0].reason}"</div>}
                          <div className="text-[11px] text-amber-900/70 mb-3">Auto-accepts in 10 days if you don't respond.</div>
                          <div className="flex gap-2">
                            <Button size="sm" disabled={prebookBusy} onClick={() => decideExtension(o.id, pending[0].id, true)} data-testid="prebook-ext-accept" className="rounded-full bg-foreground text-background">Accept new date</Button>
                            <Button size="sm" variant="outline" disabled={prebookBusy} onClick={() => decideExtension(o.id, pending[0].id, false)} data-testid="prebook-ext-reject" className="rounded-full border-amber-300 text-amber-900">Reject</Button>
                          </div>
                        </div>
                      )}

                      {/* Action buttons by status */}
                      {o.status === "prebook_offered" && (
                        <div className="flex gap-2">
                          <Button size="sm" disabled={prebookBusy} onClick={() => acceptPrebook(o.id)} data-testid="prebook-accept-btn" className="rounded-full bg-foreground text-background hover:opacity-90">
                            <DollarSign className="w-3.5 h-3.5 mr-1"/>Pay {money(pb.deposit_amount || 0)} deposit
                          </Button>
                          <Button size="sm" variant="outline" disabled={prebookBusy} onClick={() => declinePrebook(o.id)} data-testid="prebook-decline-btn" className="rounded-full">Decline (no fee)</Button>
                        </div>
                      )}
                      {o.status === "prebook_active" && isOverdue && pending.length === 0 && (
                        <Button size="sm" disabled={prebookBusy} onClick={() => cancelOverduePrebook(o.id)} data-testid="prebook-cancel-overdue-btn" className="rounded-full bg-red-600 text-white hover:bg-red-700">
                          <X className="w-3.5 h-3.5 mr-1"/>Cancel + refund deposit
                        </Button>
                      )}
                      {o.status === "cancelled" && pb.refund_status && (
                        <div className={`rounded-lg border p-3 text-[12px] ${pb.refund_status === "completed" ? "border-emerald-200 bg-emerald-50 text-emerald-900" : pb.refund_status === "escalated" ? "border-red-200 bg-red-50 text-red-900" : "border-amber-200 bg-amber-50 text-amber-900"}`} data-testid="buyer-prebook-refund-status">
                          {pb.refund_status === "completed" && <>Refund of <strong>{money(pb.deposit_amount || 0)}</strong> has been processed.</>}
                          {pb.refund_status === "pending_seller_funds" && <>Refund pending — seller's wallet is short. We're auto-retrying for up to 30 days.</>}
                          {pb.refund_status === "escalated" && <>Refund unresolved after 30 days. You can <a href={`/buyer/orders?new_claim=${o.id}#claims`} className="underline font-medium">file a claim</a> to escalate to Allianz.</>}
                        </div>
                      )}
                    </div>
                  );
                })()}

                {/* Shipping method card (existing) */}
                {(() => {
                  const mode = o.shipping_mode || "traqo";
                  const locked = ["shipped", "in_transit", "delivered", "paid", "released", "cancelled", "refunded"].includes(o.status);
                  const label = mode === "traqo" ? "verqo Shipping" : mode === "seller" ? "Seller-arranged" : "Buyer-arranged (pickup / own carrier)";
                  return (
                    <div data-testid="buyer-drawer-shipping-method">
                      <div className="text-xs uppercase tracking-widest text-foreground/60 mb-3">Shipping method</div>
                      <div className="rounded-xl border border-border p-4 space-y-2.5 text-sm bg-white">
                        <div className="flex items-center justify-between gap-3 flex-wrap">
                          <div className="min-w-0">
                            <div className="font-medium text-[14px]">{label}</div>
                            <div className="text-[11.5px] text-foreground/55 mt-0.5">
                              {locked ? "Locked — order has shipped/released." : "You can change this until the order leaves the seller's warehouse."}
                            </div>
                          </div>
                          {!locked && (
                            <Button
                              size="sm" variant="outline"
                              onClick={openChangeShipping}
                              data-testid="buyer-change-shipping-btn"
                              className="rounded-full"
                            >
                              <Truck className="w-3.5 h-3.5 mr-1.5"/> Change shipping method
                            </Button>
                          )}
                        </div>
                        {Array.isArray(o.shipping_mode_history) && o.shipping_mode_history.length > 0 && (
                          <div className="pt-2 mt-1 border-t border-border/60 space-y-1" data-testid="buyer-shipping-history">
                            <div className="text-[10.5px] mono uppercase tracking-widest text-foreground/45">History</div>
                            {o.shipping_mode_history.slice(-3).reverse().map((h, i) => {
                              const fromL = h.from === "traqo" ? "verqo" : h.from === "seller" ? "Seller" : "Buyer";
                              const toL   = h.to === "traqo" ? "verqo" : h.to === "seller" ? "Seller" : "Buyer";
                              return (
                                <div key={i} className="text-[11.5px] text-foreground/60 flex items-center gap-1.5">
                                  <span className="mono">{fromL}</span>
                                  <ArrowRight className="w-3 h-3"/>
                                  <span className="mono">{toL}</span>
                                  <span className="text-foreground/40">· {new Date(h.at).toLocaleDateString()}</span>
                                </div>
                              );
                            })}
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })()}

                {/* Tracking */}
                {drawer.shipment && (
                  <div>
                    <div className="text-xs uppercase tracking-widest text-foreground/60 mb-3">Tracking & shipping</div>
                    <div className="rounded-xl border border-border p-4 space-y-1.5 text-sm">
                      <div className="flex justify-between"><span className="text-foreground/60">Carrier</span><span>{drawer.shipment.carrier}</span></div>
                      <div className="flex justify-between"><span className="text-foreground/60">Tracking #</span><span className="mono">{drawer.shipment.tracking}</span></div>
                      <div className="flex justify-between"><span className="text-foreground/60">Route</span><span className="text-xs">{drawer.shipment.origin} → {drawer.shipment.destination}</span></div>
                      <div className="flex justify-between"><span className="text-foreground/60">Status</span><span className="chip bg-secondary">{drawer.shipment.status}</span></div>
                    </div>
                  </div>
                )}

                {/* File-a-claim shortcut — only on delivered orders. Default
                    expectation is that delivery is clean; this is the escape
                    hatch when something is wrong. Routes into the existing
                    claims module with the order pre-selected. */}
                {o.status === "delivered" && (
                  <div className="rounded-xl border border-border bg-white p-4 flex items-center gap-3 flex-wrap" data-testid="buyer-delivered-file-claim-card">
                    <AlertTriangle className="w-4 h-4 text-amber-600 shrink-0"/>
                    <div className="flex-1 text-[13px] text-foreground/80 leading-snug min-w-[180px]">
                      <span className="font-medium">Something wrong with this shipment?</span>{" "}
                      <span className="text-foreground/60">File a claim and we'll work with the seller on a refund or replacement.</span>
                    </div>
                    <Button
                      size="sm"
                      data-testid="buyer-delivered-file-claim-cta"
                      onClick={() => {
                        setDrawer(null);
                        nav(`/buyer/orders?new_claim=${o.id}#claims`);
                      }}
                      className="rounded-full bg-foreground text-background hover:bg-foreground/90"
                    >
                      File a claim <ArrowRight className="w-3.5 h-3.5 ml-1"/>
                    </Button>
                  </div>
                )}

                {/* Bilateral-consent change request — buyer-side approval
                 *  UI for any open CRQ on this order. Banner only renders when
                 *  the seller has actually requested a change; the OrderCrqGate
                 *  fetches its own data so it can stay self-contained. */}
                {drawer?.order?.id && (
                  <OrderCrqGate
                    orderId={drawer.order.id}
                    onDecided={() => openOrder(drawer.order.id)}
                  />
                )}

                {/* Payment & terms */}
                {inv && (
                  <div>
                    <div className="text-xs uppercase tracking-widest text-foreground/60 mb-3">Payment & terms</div>
                    <div className="rounded-xl border border-border p-4 space-y-1.5 text-sm">
                      <div className="flex justify-between"><span className="text-foreground/60">Invoice</span><span className="mono">{inv.code}</span></div>
                      <div className="flex justify-between"><span className="text-foreground/60">Terms</span><span>{inv.terms.toUpperCase()}</span></div>
                      <div className="flex justify-between"><span className="text-foreground/60">Status</span><span className={`chip ${inv.status === "paid" ? "bg-accent/30 border border-accent/40" : inv.status === "voided" ? "bg-foreground/10 text-foreground/55 border border-foreground/15" : "bg-secondary"}`}>{inv.status}</span></div>
                      <div className="flex justify-between"><span className="text-foreground/60">Due</span><span>{new Date(inv.due_date).toLocaleDateString()}</span></div>
                      {/* Phase IV — Validity countdown surfaces the auto-void
                       *  deadline directly inside the Buyer Order Detail drawer's
                       *  Payment & terms block. Critical because this drawer is
                       *  the buyer's go-to "view invoice" surface (clicked from
                       *  every order row). */}
                      {inv.expires_at && (
                        <div className="flex justify-between items-center pt-1.5 border-t border-border/60">
                          <span className="text-foreground/60">Validity</span>
                          <span className="inline-flex items-center gap-1.5">
                            <InvoiceExpiryChip invoice={inv} compact/>
                            <span className="text-[10.5px] text-foreground/55">
                              auto-voids on {new Date(inv.expires_at).toLocaleDateString(undefined, { month: "short", day: "numeric" })}
                            </span>
                          </span>
                        </div>
                      )}
                    </div>
                    {/* Phase IV — rose voided banner with re-issue/new-PO
                     *  guidance when the validity window already elapsed. */}
                    {(inv.status === "voided" || (inv.expires_at && new Date(inv.expires_at) < new Date() && ["draft","sent","outstanding"].includes(inv.status))) && (
                      <div className="mt-3 rounded-lg border border-rose-300 bg-rose-50/60 p-3 text-[12px] text-rose-900 flex items-start gap-2" data-testid={`order-drawer-expired-${inv.code}`}>
                        <AlertTriangle className="w-3.5 h-3.5 mt-0.5 shrink-0"/>
                        <div>
                          <div className="font-medium mb-0.5">This invoice expired</div>
                          <div className="leading-snug">
                            The seller's validity window passed before you paid or authorized. Ask the seller
                            to re-issue at the current price, or send a new PO.
                          </div>
                        </div>
                      </div>
                    )}
                    {inv.status !== "paid" && inv.status !== "draft" && inv.status !== "voided" && (
                      <div className="mt-3 space-y-2">
                        {inv.terms !== "prepaid" && hasCredit ? (
                          <>
                            <Button onClick={()=>{pay(inv.id, "authorize"); setDrawer(null);}} data-testid={`authorize-${inv.code}`}
                              className="w-full rounded-full bg-foreground text-background hover:bg-foreground/90">
                              <CheckCircle2 className="w-4 h-4 mr-1.5"/>Authorize · auto-debit on {new Date(inv.due_date).toLocaleDateString(undefined, { month: "short", day: "numeric" })}
                            </Button>
                            <Button onClick={()=>{pay(inv.id, "now"); setDrawer(null);}} variant="outline" data-testid={`pay-now-${inv.code}`}
                              className="w-full rounded-full">
                              <CreditCard className="w-4 h-4 mr-1.5"/>Pay upfront now
                            </Button>
                            <p className="text-[10px] text-foreground/50 text-center">Authorizing locks the invoice and pulls funds from your default method on the due date.</p>
                          </>
                        ) : (
                          <Button onClick={()=>{pay(inv.id, "now"); setDrawer(null);}} className="w-full rounded-full bg-foreground text-background hover:bg-foreground/90"><CreditCard className="w-4 h-4 mr-1.5"/>Pay invoice</Button>
                        )}
                      </div>
                    )}
                    {/* Seller's published trade terms — same chips that
                        ship on the invoice PDF + pack list PDF. Collapsed
                        by default to keep the drawer clean; expand for the
                        full restocking / warranty / lead-time breakdown. */}
                    <div className="mt-3">
                      <SellerPoliciesPanel invoiceId={inv.id}/>
                    </div>
                  </div>
                )}
              </div>
            </>;
          })()}
        </SheetContent>
      </Sheet>

      {/* PO Upload modal */}
      <Dialog open={poOpen} onOpenChange={(v) => { setPoOpen(v); if (!v) { setPoResult(null); setPoFile(null); } }}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="display flex items-center gap-2">
              <Sparkles className="w-5 h-5"/>AI Purchase Order Parser
            </DialogTitle>
            <div className="text-xs text-foreground/60 mt-0.5">
              Drop a PO file — verqo AI extracts every line item. Your PO is sent to {activeSellerName || "the supplier"} who will confirm pricing &amp; availability.
            </div>
          </DialogHeader>

          {!poResult && !poParsing && (
            <div className="space-y-4">
              {/* What can I upload? — verqo AI is layout-agnostic so this is
                  purely informational; no template required. The Sample CSV
                  link is a UX guarantee for first-time buyers. */}
              <div className="rounded-xl border border-border bg-secondary/30 p-4" data-testid="po-upload-help">
                <div className="flex items-center justify-between gap-3 mb-2">
                  <div className="flex items-center gap-2 text-xs font-medium">
                    <Sparkles className="w-3.5 h-3.5"/>
                    Accepted formats — no template required
                  </div>
                  <button
                    onClick={downloadSampleCSV}
                    data-testid="po-sample-csv"
                    className="text-[11px] inline-flex items-center gap-1 text-foreground/70 hover:text-foreground hover:underline"
                  >
                    <Download className="w-3 h-3"/> Download sample CSV
                  </button>
                </div>
                <ul className="text-[11.5px] text-foreground/65 space-y-1 leading-relaxed">
                  <li><strong className="text-foreground/85">PDFs</strong> — branded supplier POs, scanned/photographed POs, exported quotes</li>
                  <li><strong className="text-foreground/85">Images</strong> — JPG/PNG/HEIC/WEBP photos taken from a phone</li>
                  <li><strong className="text-foreground/85">Excel / CSV</strong> — any column names: <span className="mono text-foreground/60">Item, Description, Product, SKU#, Qty, Quantity, Units, Price, Unit Cost, Rate, Total, Line Total, Amount</span> — all map correctly</li>
                  <li><strong className="text-foreground/85">Word docs</strong> — free-form POs typed in Microsoft Word</li>
                  <li><strong className="text-foreground/85">Plain text</strong> — order emails copy-pasted into a <span className="mono">.txt</span> file</li>
                </ul>
              </div>

              <label
                htmlFor="po-file-input"
                onDragOver={(e) => { e.preventDefault(); setPoDragOver(true); }}
                onDragLeave={() => setPoDragOver(false)}
                onDrop={(e) => {
                  e.preventDefault();
                  setPoDragOver(false);
                  const f = e.dataTransfer?.files?.[0];
                  if (f) pickPoFile(f);
                }}
                data-testid="po-drop"
                className={`block w-full rounded-xl border-2 border-dashed p-10 text-center cursor-pointer transition-colors ${
                  poDragOver ? "border-foreground bg-secondary/60" : "border-border hover:border-foreground/40 hover:bg-secondary/40"
                }`}
              >
                <Upload className="w-10 h-10 mx-auto text-foreground/40"/>
                <div className="display text-lg mt-3">{poFile ? "Replace file" : "Drop your PO file here"}</div>
                <div className="text-xs text-foreground/60 mt-1">or click to browse · PDF, image, CSV, XLSX, DOCX, TXT · 25 MB max</div>
                <div className="mt-4 flex gap-1.5 justify-center text-[10px]">
                  {["PDF","XLSX","CSV","DOCX","JPG","PNG"].map(t => <span key={t} className="chip bg-secondary mono">{t}</span>)}
                </div>
                <input
                  id="po-file-input"
                  data-testid="po-file-input"
                  type="file"
                  className="hidden"
                  accept=".pdf,.png,.jpg,.jpeg,.heic,.heif,.webp,.csv,.txt,.xlsx,.xls,.docx,.doc,application/pdf,image/*,text/csv,text/plain,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.wordprocessingml.document,application/msword"
                  onChange={(e) => pickPoFile(e.target.files?.[0])}
                />
              </label>

              {poFile && (
                <div className="rounded-xl border border-border bg-secondary/30 p-3 flex items-center gap-3" data-testid="po-file-selected">
                  <div className="w-9 h-9 rounded-lg bg-foreground text-background grid place-items-center shrink-0">
                    <FileText className="w-4 h-4"/>
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-medium truncate">{poFile.name}</div>
                    <div className="text-[10px] text-foreground/60 mono">{(poFile.size/1024).toFixed(1)} KB · {poFile.type || "unknown"}</div>
                  </div>
                  <Button
                    variant="ghost" size="sm"
                    onClick={() => setPoFile(null)}
                    data-testid="po-file-clear"
                    className="rounded-full h-8 w-8 p-0"
                  >
                    <X className="w-4 h-4"/>
                  </Button>
                </div>
              )}

              <div className="flex justify-end gap-2">
                <Button variant="outline" onClick={() => setPoOpen(false)} className="rounded-full">Cancel</Button>
                <Button
                  onClick={parsePO}
                  disabled={!poFile}
                  data-testid="po-parse-btn"
                  className="rounded-full bg-foreground text-background hover:bg-foreground/90 disabled:opacity-40"
                >
                  <Sparkles className="w-4 h-4 mr-1.5"/>Parse with AI
                </Button>
              </div>
            </div>
          )}
          {poParsing && (
            <div className="py-12 text-center">
              <Sparkles className="w-10 h-10 mx-auto text-accent animate-pulse"/>
              <div className="display text-lg mt-4">{parsingLabelFor(poFile)}</div>
              <div className="text-xs text-foreground/60 mt-1">
                {FAST_EXT.has((poFile?.name?.split(".").pop() || "").toLowerCase())
                  ? "Local parse · usually under a second"
                  : "verqo AI · typically 3-6 seconds"}
              </div>
              <div className="mt-6 max-w-xs mx-auto h-1 bg-secondary rounded-full overflow-hidden">
                <div className="h-full bg-foreground animate-pulse" style={{width: "70%"}}/>
              </div>
            </div>
          )}
          {poResult && !poParsing && (() => {
            const missingPriceCount = poResult.items.filter(it => {
              const p = Number(it.unit_price);
              return !(Number.isFinite(p) && p > 0);
            }).length;
            const liveTotal = poResult.items.reduce((s, it) => {
              const p = Number(it.unit_price);
              return s + (Number.isFinite(p) ? p * it.qty : 0);
            }, 0);
            return (
            <div className="space-y-4">
              <div className="rounded-xl p-4 flex items-center gap-3 border bg-accent/20 border-accent/40">
                <Check className="w-5 h-5"/>
                <div className="flex-1">
                  <div className="text-sm font-medium">
                    {poResult.items.length} line{poResult.items.length>1?'s':''} extracted from your PO
                  </div>
                  <div className="text-xs text-foreground/70">{poResult.filename} · {poResult.processed_in_ms}ms · {poResult.confidence}% confidence · {poResult.model}</div>
                </div>
              </div>

              <div className="text-[11px] text-foreground/60 -mt-1">
                {activeSellerName || "Your supplier"} will receive these lines exactly as extracted (including any edits you make below) and confirm pricing &amp; availability on their side. Edit unit prices if the AI couldn&apos;t read them.
              </div>

              <div className="rounded-xl border border-border overflow-hidden">
                <table className="w-full text-sm">
                  <thead className="bg-secondary/50 text-left text-[10px] uppercase tracking-widest text-foreground/60">
                    <tr><th className="px-3 py-2">Brand</th><th className="px-3 py-2">Product / SKU</th><th className="px-3 py-2">Qty</th><th className="px-3 py-2">Unit</th><th className="px-3 py-2">Total</th></tr>
                  </thead>
                  <tbody className="divide-y divide-border">
                    {poResult.items.map((it, i) => {
                      const priceNum = Number(it.unit_price);
                      const hasPrice = Number.isFinite(priceNum) && priceNum > 0;
                      const lineTotal = hasPrice ? priceNum * it.qty : null;
                      return (
                      <tr key={i}>
                        <td className="px-3 py-2 text-xs">{it.brand || "—"}</td>
                        <td className="px-3 py-2"><div className="font-medium">{it.product}</div><div className="text-[10px] mono text-foreground/50">{it.sku || "no SKU"}</div></td>
                        <td className="px-3 py-2">{it.qty}</td>
                        <td className="px-3 py-2">
                          <input
                            type="number"
                            step="0.01"
                            min="0"
                            value={it.unit_price}
                            onChange={(e) => editPoItem(i, { unit_price: e.target.value })}
                            placeholder="0.00"
                            data-testid={`po-price-input-${i}`}
                            className={`w-20 rounded border px-2 py-1 text-xs mono focus:outline-none focus:ring-1 focus:ring-foreground/30 ${hasPrice ? "border-border bg-white" : "border-amber-300 bg-amber-50"}`}
                          />
                        </td>
                        <td className="px-3 py-2 display">{lineTotal != null ? money(lineTotal) : <span className="text-[10px] text-amber-700">needs price</span>}</td>
                      </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              {/* ── Shipping handler picker (Layer 1 of buyer-arranged feature) ── */}
              <div className="rounded-xl border border-border p-4 mt-2 space-y-3" data-testid="po-shipping-picker">
                <div className="flex items-center justify-between gap-3">
                  <div>
                    <div className="text-[13px] font-medium">How will this be shipped?</div>
                    <div className="text-[11.5px] text-foreground/60">Pick once for the whole PO — you can change it on the order page before pickup.</div>
                  </div>
                </div>
                <div className="grid sm:grid-cols-3 gap-2">
                  {[
                    { id: "traqo",          label: "verqo Shipping",       sub: "Managed · insured · tracked",   recommended: true,  show: true },
                    // "Seller arranges" only shows when the active supplier has explicitly
                    // opted in (tenants.settings.profile.offer_seller_arranged_shipping).
                    // Default is OFF — shipping is the buyer's responsibility in B2B wholesale.
                    { id: "seller",         label: "Seller arranges",      sub: "Seller's preferred carrier",     recommended: false, show: !!(connectionsBySellerId.get(activeSellerId)?.offer_seller_arranged_shipping) },
                    { id: "buyer_arranged", label: "I'll arrange shipping", sub: "Pickup or my own carrier",      recommended: false, show: true },
                  ].filter(o => o.show).map(opt => {
                    const on = poShippingMode === opt.id;
                    return (
                      <button
                        type="button"
                        key={opt.id}
                        onClick={() => setPoShippingMode(opt.id)}
                        data-testid={`po-shipping-${opt.id}`}
                        className={`text-left p-3 rounded-lg border transition-colors ${
                          on ? "border-foreground bg-foreground/[0.04]"
                             : "border-border hover:border-foreground/30"
                        }`}
                      >
                        <div className="flex items-center justify-between">
                          <div className="text-[13px] font-medium">{opt.label}</div>
                          {opt.recommended && (
                            <span className="text-[9px] mono uppercase tracking-widest px-1.5 py-0.5 rounded bg-emerald-500/15 text-emerald-700 border border-emerald-500/30">Recommended</span>
                          )}
                        </div>
                        <div className="text-[11px] text-foreground/55 mt-1">{opt.sub}</div>
                        {on && (
                          <div className="text-[10px] mono uppercase tracking-widest text-foreground/40 mt-2 flex items-center gap-1">
                            <span className="w-1.5 h-1.5 rounded-full bg-foreground"/> Selected
                          </div>
                        )}
                      </button>
                    );
                  })}
                </div>
                {poShippingMode === "buyer_arranged" && (
                  <div className="rounded-lg border border-amber-300 bg-amber-50 p-3 space-y-2" data-testid="po-shipping-waiver">
                    <div className="text-[12.5px] font-medium text-amber-900">Liability waiver</div>
                    <p className="text-[12px] text-amber-900/85 leading-snug">
                      I'm arranging my own shipping (pickup or my own carrier). verqo and the seller are <strong>not responsible</strong> for loss, damage, or delay once the goods leave the seller's warehouse. Transit-related claims (damage/lost-in-transit) are <strong>not available</strong> for this order. Quality and wrong-item claims still apply.
                    </p>
                    <label className="flex items-start gap-2 text-[12.5px] text-amber-900 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={poWaiverAccepted}
                        onChange={(e) => setPoWaiverAccepted(e.target.checked)}
                        data-testid="po-shipping-waiver-checkbox"
                        className="mt-0.5 w-4 h-4 rounded border-amber-400"
                      />
                      <span>I accept these terms and confirm I'm arranging my own shipping.</span>
                    </label>
                  </div>
                )}
              </div>

              <div className="flex justify-between items-center pt-2">
                <div>
                  <div className="text-xs text-foreground/60">PO total</div>
                  <div className="display text-2xl">{money(liveTotal)}</div>
                  {missingPriceCount > 0 && (
                    <div className="text-[11px] text-amber-700 mt-0.5">{missingPriceCount} row{missingPriceCount>1?'s':''} need a price</div>
                  )}
                </div>
                <div className="flex gap-2">
                  <Button variant="outline" onClick={()=>{ setPoResult(null); setPoFile(null); }} className="rounded-full">Upload another</Button>
                  <Button
                    onClick={confirmPO}
                    disabled={poSubmitting || missingPriceCount > 0 || (poShippingMode === "buyer_arranged" && !poWaiverAccepted)}
                    data-testid="po-confirm"
                    className="rounded-full text-background hover:opacity-90 disabled:opacity-40 bg-foreground"
                  >
                    {poSubmitting ? "Sending…" : `Send PO to ${activeSellerName || "supplier"}`}
                  </Button>
                </div>
              </div>
            </div>
            );
          })()}
        </DialogContent>
      </Dialog>

      {/* ── Change shipping method modal (buyer-only, post-prep) ─────────── */}
      <Dialog open={changeShipOpen} onOpenChange={setChangeShipOpen}>
        <DialogContent className="max-w-lg" data-testid="change-shipping-dialog">
          <DialogHeader>
            <DialogTitle>Change shipping method</DialogTitle>
          </DialogHeader>
          {(() => {
            const o = drawer?.order;
            if (!o) return null;
            const conn = (connections || []).find(c => c.tenant_owner_user_id === o.seller_id);
            const sellerOffers = !!conn?.offer_seller_arranged_shipping;
            const options = [
              { id: "traqo",          label: "verqo Shipping",       sub: "Managed · insured · tracked",  recommended: true,  show: true },
              { id: "seller",         label: "Seller arranges",      sub: "Seller's preferred carrier",   recommended: false, show: sellerOffers },
              { id: "buyer_arranged", label: "I'll arrange shipping", sub: "Pickup or my own carrier",     recommended: false, show: true },
            ].filter(opt => opt.show);
            return (
              <div className="space-y-3">
                <p className="text-[12.5px] text-foreground/60 leading-snug">
                  Switch how this order ships. The seller will be notified. You can change this any time before the goods leave the warehouse.
                </p>
                <div className="grid sm:grid-cols-3 gap-2">
                  {options.map(opt => {
                    const on = changeShipMode === opt.id;
                    return (
                      <button
                        type="button"
                        key={opt.id}
                        onClick={() => setChangeShipMode(opt.id)}
                        data-testid={`change-shipping-${opt.id}`}
                        className={`text-left p-3 rounded-lg border transition-colors ${
                          on ? "border-foreground bg-foreground/[0.04]" : "border-border hover:border-foreground/30"
                        }`}
                      >
                        <div className="flex items-center justify-between">
                          <div className="text-[13px] font-medium">{opt.label}</div>
                          {opt.recommended && (
                            <span className="text-[9px] mono uppercase tracking-widest px-1.5 py-0.5 rounded bg-emerald-500/15 text-emerald-700 border border-emerald-500/30">Recommended</span>
                          )}
                        </div>
                        <div className="text-[11px] text-foreground/55 mt-1">{opt.sub}</div>
                      </button>
                    );
                  })}
                </div>
                {!sellerOffers && (
                  <div className="text-[11.5px] text-foreground/55">
                    This seller doesn't offer seller-arranged shipping for this order.
                  </div>
                )}
                {changeShipMode === "buyer_arranged" && (
                  <div className="rounded-lg border border-amber-300 bg-amber-50 p-3 space-y-2" data-testid="change-shipping-waiver">
                    <div className="text-[12.5px] font-medium text-amber-900">Liability waiver</div>
                    <p className="text-[12px] text-amber-900/85 leading-snug">
                      I'm arranging my own shipping. verqo and the seller are <strong>not responsible</strong> for loss, damage, or delay once the goods leave the seller's warehouse. Transit-related claims (damage/lost-in-transit) are <strong>not available</strong> on this order.
                    </p>
                    <label className="flex items-start gap-2 text-[12.5px] text-amber-900 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={changeShipWaiver}
                        onChange={(e) => setChangeShipWaiver(e.target.checked)}
                        data-testid="change-shipping-waiver-checkbox"
                        className="mt-0.5 w-4 h-4 rounded border-amber-400"
                      />
                      <span>I accept these terms and confirm I'm arranging my own shipping.</span>
                    </label>
                  </div>
                )}
                <div className="flex justify-end gap-2 pt-2">
                  <Button variant="outline" onClick={() => setChangeShipOpen(false)} className="rounded-full" data-testid="change-shipping-cancel">Cancel</Button>
                  <Button
                    onClick={submitChangeShipping}
                    disabled={changeShipSaving || (changeShipMode === "buyer_arranged" && !changeShipWaiver) || changeShipMode === (o.shipping_mode || "traqo")}
                    data-testid="change-shipping-confirm"
                    className="rounded-full bg-foreground text-background hover:opacity-90 disabled:opacity-40"
                  >
                    {changeShipSaving ? "Saving…" : "Update shipping method"}
                  </Button>
                </div>
              </div>
            );
          })()}
        </DialogContent>
      </Dialog>

      {/* ── Buyer cancel order modal (with restocking-fee preview) ────── */}
      <Dialog open={cancelOpen} onOpenChange={setCancelOpen}>
        <DialogContent className="max-w-md" data-testid="buyer-cancel-dialog">
          <DialogHeader>
            <DialogTitle>Cancel this order?</DialogTitle>
          </DialogHeader>
          {!cancelPreview ? (
            <div className="py-6 text-center text-[12.5px] text-foreground/55">Calculating fee preview…</div>
          ) : (
            <div className="space-y-3">
              <div className="rounded-xl border border-border p-4 space-y-2 bg-secondary/30">
                <div className="flex justify-between text-[13px]"><span className="text-foreground/60">Order total</span><span className="mono">${cancelPreview.order_total.toFixed(2)}</span></div>
                {cancelPreview.was_packed ? (
                  <>
                    <div className="flex justify-between text-[13px]"><span className="text-foreground/60">Restocking fee ({cancelPreview.restocking_pct.toFixed(1)}%)</span><span className="mono text-red-600">−${cancelPreview.restocking_fee.toFixed(2)}</span></div>
                    <div className="flex justify-between text-[14px] font-medium pt-2 border-t border-border"><span>Refund to you</span><span className="mono">${cancelPreview.refund_amount.toFixed(2)}</span></div>
                  </>
                ) : (
                  <div className="flex justify-between text-[14px] font-medium pt-2 border-t border-border"><span>Refund to you</span><span className="mono">${(cancelPreview.invoice_was_paid ? cancelPreview.refund_amount : 0).toFixed(2)}{!cancelPreview.invoice_was_paid && " (no payment yet)"}</span></div>
                )}
              </div>
              {cancelPreview.was_packed && (
                <div className="rounded-lg border border-amber-300 bg-amber-50 p-3 text-[12px] text-amber-900 leading-snug" data-testid="buyer-cancel-fee-warning">
                  <strong>Heads up:</strong> The seller has already packed this order, so a restocking fee applies under their policy ({cancelPreview.restocking_pct.toFixed(1)}% of total). They may waive it at their discretion after cancellation.
                </div>
              )}
              <div>
                <label className="text-[12px] text-foreground/60 mb-1 block">Reason (optional)</label>
                <Input
                  value={cancelReason}
                  onChange={e => setCancelReason(e.target.value.slice(0, 240))}
                  placeholder="Changed mind, ordered wrong items, …"
                  data-testid="buyer-cancel-reason"
                  className="h-9"
                />
              </div>
              <div className="flex justify-end gap-2 pt-1">
                <Button variant="outline" onClick={() => setCancelOpen(false)} className="rounded-full" data-testid="buyer-cancel-back">Back</Button>
                <Button
                  onClick={submitCancel}
                  disabled={cancelSaving}
                  data-testid="buyer-cancel-confirm"
                  className="rounded-full bg-red-600 text-white hover:bg-red-700 disabled:opacity-40"
                >
                  {cancelSaving ? "Cancelling…" : (cancelPreview.was_packed ? `Confirm cancel · −$${cancelPreview.restocking_fee.toFixed(2)} fee` : "Confirm cancel")}
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// JourneyFocusMode — single-pane "checkout-style" journey navigator.
// Renders a sticky horizontal stepper at the top + ONE focused step panel
// below it. Buyer never has to scroll the page to advance — clicking a step
// or the Next button swaps the panel content in place with a slide animation.
// ─────────────────────────────────────────────────────────────────────────────
function JourneyFocusMode({
  activeStep, setActiveStep, counts, allSteps, hasCatalog, invoices,
  journeyOrders, openOrder, pay, nav, setPoOpen, activeSellerName,
}) {
  const idx = allSteps.findIndex(s => s.id === activeStep);
  const current = allSteps[idx] || allSteps[0];
  const prev = idx > 0 ? allSteps[idx - 1] : null;
  const next = idx < allSteps.length - 1 ? allSteps[idx + 1] : null;
  const stageMeta = STAGE_META[current.id];

  // Animation key — re-mounts the focus pane to play slide-in
  const paneKey = `pane-${current.id}`;

  return (
    <div className="space-y-5">
      {/* ─── Sticky horizontal stepper ─── */}
      <div className="sticky top-[110px] z-10 -mx-4 sm:-mx-6 lg:-mx-8 px-4 sm:px-6 lg:px-8 py-3 bg-background/85 backdrop-blur border-b border-border">
        <div className="flex items-center justify-between gap-1 overflow-x-auto" data-testid="journey-stepper">
          {allSteps.map((s, i) => {
            const ct = counts[s.id] || 0;
            const isActive = s.id === activeStep;
            const isPast = i < idx;
            const meta = STAGE_META[s.id];
            return (
              <div key={s.id} className="flex items-center flex-1 min-w-0 last:flex-none">
                <button
                  onClick={() => setActiveStep(s.id)}
                  data-testid={`step-${s.num}`}
                  className="group flex flex-col items-center gap-1.5 py-1 min-w-0"
                >
                  <div className={`w-9 h-9 rounded-full grid place-items-center text-xs font-medium transition-all relative shrink-0 ${
                    isActive
                      ? "bg-foreground text-background scale-110 shadow-md ring-4 ring-foreground/10"
                      : isPast
                        ? "bg-foreground/85 text-background"
                        : ct > 0
                          ? "bg-white text-foreground border-2 border-foreground/35"
                          : "bg-secondary text-foreground/45 border border-border"
                  }`}>
                    {isPast && !isActive ? <Check className="w-4 h-4"/> : s.num}
                    {ct > 0 && !isActive && (
                      <span className="absolute -top-1 -right-1 min-w-[16px] h-[16px] px-1 rounded-full bg-rose-500 text-white text-[9px] font-medium grid place-items-center ring-2 ring-background">
                        {ct}
                      </span>
                    )}
                  </div>
                  <div className={`text-[11px] font-medium leading-tight text-center whitespace-nowrap ${
                    isActive ? "text-foreground" : "text-foreground/55 group-hover:text-foreground/85"
                  }`}>
                    {meta?.label || s.label}
                  </div>
                </button>
                {i < allSteps.length - 1 && (
                  <div className={`flex-1 h-px mx-2 transition-colors ${i < idx ? "bg-foreground/85" : "bg-border"}`} />
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* ─── Focus pane (single step) ─── */}
      <div key={paneKey} className="animate-step-in">
        {current.id === "place" && (
          <StepPlace hasCatalog={hasCatalog} nav={nav} setPoOpen={setPoOpen} />
        )}
        {current.id === "cart" && (
          <StepCart nav={nav} />
        )}
        {current.id === "seller_confirm" && (
          journeyOrders("seller_confirm").length > 0 ? (
            <JourneySection step={3} stage="seller_confirm"
              title="Waiting for seller to confirm"
              subtitle="Once the seller confirms, your invoice will be issued and you'll move to the next step."
              orders={journeyOrders("seller_confirm")} openOrder={openOrder} pay={pay}/>
          ) : (
            <EmptyStep step={3} title={`No ${activeSellerName ? activeSellerName + " " : ""}orders awaiting confirmation`}
              subtitle={`When you place an order${activeSellerName ? ` with ${activeSellerName}` : ""}, it'll wait here for the supplier to confirm and issue an invoice. Usually takes ~2 business hours.`}
              cta={{ label: "Start a new order", action: () => setActiveStep("place") }} />
          )
        )}
        {current.id === "pay" && (
          journeyOrders("pay").length > 0 ? (
            <JourneySection step={4} stage="pay"
              title="Pay your invoice to start fulfilment"
              subtitle="Seller confirmed your order. Pay the invoice — once paid the seller will begin picking & packing."
              orders={journeyOrders("pay")} openOrder={openOrder} pay={pay}/>
          ) : (
            <EmptyStep step={4} title="No invoices to pay right now"
              subtitle={`All confirmed${activeSellerName ? ` ${activeSellerName}` : ""} orders are paid · ${invoices.filter(i=>i.status==="paid").length} invoices settled this period.`}
              cta={{ label: "Open Payments portal", action: () => nav("/buyer/payments") }} />
          )
        )}
        {current.id === "pack" && (
          journeyOrders("pack").length > 0 ? (
            <JourneySection step={5} stage="pack"
              title="Seller is packing your orders"
              subtitle="Payment received ✓ — supplier is picking, packing and confirming dimensions."
              orders={journeyOrders("pack")} openOrder={openOrder} pay={pay}/>
          ) : (
            <EmptyStep step={5} title="Nothing being prepared right now"
              subtitle={`Once you pay an invoice, ${activeSellerName || "your supplier"} starts picking and packing. You'll see progress here.`}
              cta={null} />
          )
        )}
        {current.id === "shipped" && (
          journeyOrders("shipped").length > 0 ? (
            <JourneySection step={6} stage="shipped"
              title="Orders ready to ship — schedule a carrier"
              subtitle="Seller has packed and confirmed dimensions. Track or book a carrier to deliver to your door."
              orders={journeyOrders("shipped")} openOrder={openOrder} pay={pay} ship/>
          ) : (
            <EmptyStep step={6} title="Nothing shipping right now"
              subtitle={`When ${activeSellerName || "your supplier"} finishes packing, your shipments appear here for booking and tracking.`}
              cta={{ label: "Open Shipping", action: () => nav("/buyer/shipping") }} />
          )
        )}
      </div>

      {/* ─── Footer Prev / Next + What's next preview ─── */}
      <div className="rounded-2xl bg-secondary/30 border border-border p-4 flex items-center justify-between gap-3 flex-wrap" data-testid="journey-footer">
        <Button
          variant="outline"
          disabled={!prev}
          onClick={() => prev && setActiveStep(prev.id)}
          data-testid="journey-prev"
          className="rounded-full h-10 px-4 disabled:opacity-30"
        >
          <ArrowLeft className="w-4 h-4 mr-1.5"/>
          {prev ? <>Back · <span className="text-foreground/55 ml-1">{(STAGE_META[prev.id]?.label) || prev.label}</span></> : "Back"}
        </Button>

        {next && (
          <div className="text-xs text-foreground/55 order-3 sm:order-2 w-full sm:w-auto text-center">
            {(counts[next.id] || 0) > 0
              ? <>Next: <span className="text-foreground font-medium">{counts[next.id]} {counts[next.id] === 1 ? "order" : "orders"}</span> in {STAGE_META[next.id]?.label || next.label}</>
              : <>Next step: <span className="text-foreground font-medium">{STAGE_META[next.id]?.label || next.label}</span></>}
          </div>
        )}

        <Button
          disabled={!next}
          onClick={() => next && setActiveStep(next.id)}
          data-testid="journey-next"
          className="rounded-full h-10 px-4 bg-foreground text-background hover:bg-foreground/90 disabled:opacity-30 order-2 sm:order-3"
        >
          {next ? <>Continue · {STAGE_META[next.id]?.label || next.label}</> : "All done"}
          <ArrowRight className="w-4 h-4 ml-1.5"/>
        </Button>
      </div>
    </div>
  );
}

// ─── Step 1 — Place new order (upload PO only) ───
// Catalog browsing is currently disabled (Catalog app is "coming soon"),
// so PO upload is the only available path. The component still accepts
// `hasCatalog` to stay API-compatible with the parent — when the catalog
// app ships, restore the two-card grid + install banner (see git history).
// Catalog tile is gated by `hasCatalog` (true ONLY when the buyer is
// connected to a supplier that has the Catalog app enabled — currently
// limited to the BRND Direct private beta).
function StepPlace({ hasCatalog, nav, setPoOpen }) {
  return (
    <section className="rounded-2xl bg-white border border-border p-7" data-testid="focus-place">
      <div className="text-xs uppercase tracking-widest text-foreground/50">Step 1 of 6 — Your action</div>
      <h2 className="display text-2xl mt-1">Start a new wholesale order</h2>
      <p className="text-sm text-foreground/60 mt-1">
        {hasCatalog
          ? "Browse your supplier's catalog OR upload your own PO — both paths land in the same cart."
          : "Upload a Purchase Order document — your supplier will confirm pricing and availability."}
      </p>
      <div className={`grid ${hasCatalog ? "md:grid-cols-2" : "md:grid-cols-1 max-w-xl"} gap-4 mt-6`}>
        {hasCatalog && (
          <button onClick={() => nav("/buyer/catalog")} data-testid="step1-catalog"
            className="text-left rounded-xl border border-border p-5 hover:border-foreground/40 hover:bg-secondary/40 transition-all group">
            <div className="flex items-center justify-between">
              <div className="w-10 h-10 rounded-lg bg-foreground text-accent grid place-items-center"><ShoppingBag className="w-5 h-5"/></div>
              <span className="chip bg-emerald-50 border border-emerald-200 text-emerald-700">Live catalog</span>
            </div>
            <div className="display text-lg mt-4">Browse catalog</div>
            <p className="text-xs text-foreground/60 mt-1">Shop your supplier's live linesheet — pricing, MOQ and stock visible per SKU. Add items to cart and checkout in seconds.</p>
            <div className="text-xs mt-3 text-foreground/70 inline-flex items-center group-hover:gap-2 transition-all">Open catalog <ArrowUpRight className="w-3 h-3 ml-1"/></div>
          </button>
        )}
        <button onClick={() => setPoOpen(true)} data-testid="step1-po"
          className="text-left rounded-xl border border-border p-5 hover:border-foreground/40 hover:bg-secondary/40 transition-all group">
          <div className="flex items-center justify-between">
            <div className="w-10 h-10 rounded-lg bg-foreground text-accent grid place-items-center"><Sparkles className="w-5 h-5"/></div>
            <span className="chip bg-secondary">{hasCatalog ? "AI-powered" : "Quickest path"}</span>
          </div>
          <div className="display text-lg mt-4">Upload Purchase Order</div>
          <p className="text-xs text-foreground/60 mt-1">AI extracts every line item from PDF, Excel, CSV or image — your supplier confirms pricing &amp; availability.</p>
          <div className="text-xs mt-3 text-foreground/70 inline-flex items-center group-hover:gap-2 transition-all">Upload PO <ArrowUpRight className="w-3 h-3 ml-1"/></div>
        </button>
      </div>
      {!hasCatalog && (
        <div className="mt-4 rounded-xl bg-secondary/30 border border-border p-4 text-xs text-foreground/65 flex items-center justify-between">
          <span>Catalog browsing is coming soon — for now, every order starts with a PO.</span>
        </div>
      )}
    </section>
  );
}

// ─── Step 2 — Confirm cart ───
function StepCart({ nav }) {
  return (
    <section className="rounded-2xl bg-white border border-border p-7" data-testid="focus-cart">
      <div className="flex items-start justify-between gap-4 flex-wrap mb-5">
        <div>
          <div className="text-xs uppercase tracking-widest text-foreground/50">Step 2 of 6 — Your action</div>
          <h2 className="display text-2xl mt-1">Review &amp; confirm your cart</h2>
          <p className="text-sm text-foreground/60 mt-1">Once your cart looks good, place the order. The seller will then confirm it and issue an invoice — no payment is taken yet.</p>
        </div>
        <Button onClick={()=>nav("/buyer/cart")} data-testid="step2-open-cart"
          className="rounded-full bg-foreground text-background hover:bg-foreground/90 h-10 px-4">
          <ShoppingBag className="w-4 h-4 mr-1.5"/>Open cart
        </Button>
      </div>
      <div className="grid md:grid-cols-3 gap-3">
        {[
          { icon: Check, t: "Adjust quantities", d: "Validate MOQ minimums per SKU" },
          { icon: ShoppingBag, t: "Multi-seller cart", d: "One checkout, separate orders per supplier" },
          { icon: Clock, t: "No charges yet", d: "Payment only after seller confirmation" },
        ].map((c, i) => (
          <div key={i} className="rounded-xl border border-border p-4 bg-secondary/30">
            <div className="w-9 h-9 rounded-lg bg-white border border-border grid place-items-center"><c.icon className="w-4 h-4"/></div>
            <div className="text-sm font-medium mt-3">{c.t}</div>
            <div className="text-xs text-foreground/55 mt-0.5">{c.d}</div>
          </div>
        ))}
      </div>
    </section>
  );
}

// ─── Empty state for stages with no orders ───
function EmptyStep({ step, title, subtitle, cta }) {
  return (
    <section className="rounded-2xl bg-white border border-border p-10 text-center" data-testid={`focus-empty-${step}`}>
      <div className="w-14 h-14 rounded-2xl bg-emerald-50 text-emerald-600 grid place-items-center mx-auto">
        <Check className="w-6 h-6" strokeWidth={2.5}/>
      </div>
      <div className="text-xs mono uppercase tracking-[0.18em] text-foreground/45 mt-5">Step {step} of 6</div>
      <h2 className="display text-2xl mt-1.5 tracking-tight">{title}</h2>
      <p className="text-sm text-foreground/55 mt-2 max-w-md mx-auto">{subtitle}</p>
      {cta && (
        <Button
          onClick={cta.action}
          className="mt-6 rounded-full bg-foreground text-background hover:bg-foreground/90 h-10 px-5"
          data-testid={`focus-empty-cta-${step}`}
        >
          {cta.label}
          <ArrowRight className="w-4 h-4 ml-1.5"/>
        </Button>
      )}
    </section>
  );
}

function JourneySection({ step, stage, title, subtitle, orders, openOrder, pay, ship }) {
  const meta = STAGE_META[stage];
  return (
    <section id={`step-${step}`} className="rounded-2xl bg-white border border-border p-6">
      <div className="flex items-start justify-between gap-4 mb-4 flex-wrap">
        <div>
          <div className="text-xs uppercase tracking-widest text-foreground/50">Step {step} of 6 — {step === 4 || step === 6 ? "Your action required" : "Seller action"}</div>
          <h2 className="display text-xl mt-1">{title}</h2>
          <p className="text-sm text-foreground/60 mt-0.5">{subtitle}</p>
        </div>
        <span className={`chip ${meta.chip}`}>{meta.emoji} {orders.length} {orders.length===1?"order":"orders"}</span>
      </div>
      <div className="space-y-3">
        {orders.map(o => {
          const inv = o.invoice;
          const main = (o.items || [])[0];
          const totalUnits = (o.items || []).reduce((s,i)=>s+(i.qty||0), 0);
          return (
            <div key={o.id} className="rounded-xl border border-border p-4 hover:border-foreground/40" data-testid={`journey-${o.code}`}>
              <div className="flex items-start justify-between gap-4 flex-wrap">
                <div className="flex items-center gap-3 min-w-0 flex-wrap">
                  <span className="mono text-xs text-foreground/60">{o.code}</span>
                  <span className="font-medium">{o.seller_name}</span>
                  <span className={`chip ${meta.chip}`}>{meta.emoji} {meta.label}</span>
                  <span className="text-xs text-foreground/50">{new Date(o.created_at).toLocaleDateString()}</span>
                  {/* ETA chip — context-aware per stage */}
                  {stage === "seller_confirm" && o.expected_invoice_at && (
                    <span className="chip bg-amber-100 text-amber-800 border border-amber-200 text-[10px]" data-testid={`eta-invoice-${o.code}`}>
                      <Clock className="w-2.5 h-2.5"/>Invoice expected {formatETA(o.expected_invoice_at)}
                    </span>
                  )}
                  {(stage === "pay" || stage === "pack") && o.expected_ready_at && (
                    <span className="chip bg-blue-100 text-blue-800 border border-blue-200 text-[10px]" data-testid={`eta-ready-${o.code}`}>
                      <Package className="w-2.5 h-2.5"/>Ready to ship by {new Date(o.expected_ready_at).toLocaleDateString(undefined, { month: "short", day: "numeric" })}
                    </span>
                  )}
                </div>
              </div>
              <div className="mt-3 grid grid-cols-2 md:grid-cols-5 gap-3 text-sm">
                <div><div className="text-[10px] uppercase tracking-widest text-foreground/50">Product</div><div className="truncate">{main?.name}</div></div>
                <div><div className="text-[10px] uppercase tracking-widest text-foreground/50">Units</div><div>{totalUnits}</div></div>
                <div><div className="text-[10px] uppercase tracking-widest text-foreground/50">Total</div><div className="display">{money(o.total)}</div></div>
                {inv && <div><div className="text-[10px] uppercase tracking-widest text-foreground/50">Invoice</div><div className="mono text-xs">{inv.code}</div></div>}
                {inv && <div><div className="text-[10px] uppercase tracking-widest text-foreground/50">Due</div><div className="text-xs">{new Date(inv.due_date).toLocaleDateString()}</div></div>}
              </div>
              {/* Phase IV — Invoice validity countdown for the "Pay" stage.
               *  Buyers need to see this directly on the journey card so they
               *  know how long they have to pay or authorize before the
               *  invoice auto-voids. Shows for sent/outstanding invoices only. */}
              {stage === "pay" && inv && (
                <div className="mt-2 flex items-center gap-2">
                  <span className="text-[10px] uppercase tracking-widest text-foreground/50">Validity</span>
                  <InvoiceExpiryChip invoice={inv} compact/>
                  {inv.expires_at && (
                    <span className="text-[10.5px] text-foreground/55">
                      · auto-voids on {new Date(inv.expires_at).toLocaleDateString(undefined, { month: "short", day: "numeric" })}
                    </span>
                  )}
                </div>
              )}
              <div className="mt-4 flex gap-2 justify-end">
                <Button size="sm" variant="ghost" onClick={()=>openOrder(o.id)}>View</Button>
                {stage === "pay" && inv && <Button size="sm" onClick={()=>pay(inv.id)} data-testid={`journey-pay-${o.code}`} className="rounded-full bg-foreground text-background hover:bg-foreground/90">Pay now — {money(inv.amount)}</Button>}
                {stage === "shipped" && <Button size="sm" onClick={()=>window.location.assign("/buyer/shipping")} data-testid={`journey-ship-${o.code}`} className="rounded-full bg-foreground text-background hover:bg-foreground/90">{ship ? "Schedule shipment" : "Track"}</Button>}
                {stage === "pack" && <Button size="sm" onClick={()=>openOrder(o.id)} className="rounded-full" variant="outline">Check status</Button>}
              </div>
            </div>
          );
        })}
      </div>
    </section>
  );
}


function formatETA(iso) {
  const dt = new Date(iso);
  const diff = dt - Date.now();
  const hours = Math.round(diff / 3600000);
  if (Math.abs(hours) < 24) {
    if (hours <= 0) return "any moment now";
    return `in ~${hours}h`;
  }
  const days = Math.round(hours / 24);
  if (days === 1) return "tomorrow";
  if (days < 7) return `in ${days} days`;
  return dt.toLocaleDateString(undefined, { month: "short", day: "numeric" });
}

// ── Inline invoice download chip — same pattern as seller table.
//   Phase IV: also surfaces the <InvoiceExpiryChip/> countdown next to
//   the download button so the buyer sees the validity window directly
//   on the order row, not only on the Payments tab.
function InvoiceDocCell({ invoice }) {
  if (!invoice?.id) return <span className="text-foreground/30 text-xs">—</span>;
  const dl = async (e) => {
    e.stopPropagation();
    try {
      const { data } = await api.get(`/invoices/${invoice.id}/pdf`, { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `invoice-${invoice.code || invoice.id.slice(0, 8)}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
    } catch { toast.error("Invoice download failed"); }
  };
  return (
    <div className="inline-flex flex-col items-start gap-1">
      <button
        type="button" onClick={dl} title="Download invoice PDF"
        data-testid={`invoice-pdf-${invoice.code}`}
        className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-secondary border border-border hover:bg-secondary/80"
      >
        <FileText className="w-3 h-3 text-foreground/55"/>
        <span className="font-mono text-[11.5px]">{invoice.code}</span>
        <Download className="w-3 h-3 text-foreground/60 ml-0.5"/>
      </button>
      <InvoiceExpiryChip invoice={invoice} compact/>
    </div>
  );
}

// ── Inline pack-list download chip — only lights up after seller enters
// shipping info (status `transit` / `ready_for_pickup` / `delivered`).
// PDF stamps the seller's published trade terms + custom T&C.
function PackListDocCell({ order }) {
  if (!order?.id) return <span className="text-foreground/30 text-xs">—</span>;
  const ready = ["ready_for_pickup", "transit", "delivered"].includes(order.status);
  if (!ready) return <span className="text-foreground/30 text-xs" title="Available after seller enters shipping info">—</span>;
  const dl = async (e) => {
    e.stopPropagation();
    try {
      const { data } = await api.get(`/orders/${order.id}/pack-list/pdf`, { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `pack-list-${order.code || order.id.slice(0, 8)}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
    } catch { toast.error("Pack list download failed"); }
  };
  return (
    <button
      type="button" onClick={dl} title="Download pack list PDF"
      data-testid={`pack-list-pdf-${order.code}`}
      className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-secondary border border-border hover:bg-secondary/80"
    >
      <Package className="w-3 h-3 text-foreground/55"/>
      <span className="font-mono text-[11.5px]">PL-{(order.code || "").replace(/^ORD-/, "")}</span>
      <Download className="w-3 h-3 text-foreground/60 ml-0.5"/>
    </button>
  );
}

// ── Inline tracking chip — only renders when the seller has entered
// shipping info AND tracking is non-empty. Shows carrier + truncated
// tracking + click-to-copy. Mirrors the seller-side TrackingCell.
function TrackingDocCell({ order }) {
  const tracking = order?.shipment?.tracking || order?.tracking;
  if (!tracking) return null;
  const carrier = (order?.shipment?.carrier || "").toUpperCase();
  const display = tracking.length > 16 ? tracking.slice(0, 12) + "…" : tracking;
  const copy = async (e) => {
    e.stopPropagation();
    try {
      await navigator.clipboard.writeText(tracking);
      toast.success("Tracking copied");
    } catch { /* clipboard blocked — silent */ }
  };
  return (
    <button
      type="button" onClick={copy} title={`Copy tracking · ${tracking}`}
      data-testid={`buyer-tracking-${order.code}`}
      className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-secondary border border-border hover:bg-secondary/80"
    >
      <Truck className="w-3 h-3 text-foreground/55"/>
      <span className="font-mono text-[11.5px]">{display}</span>
      {carrier && (
        <span className="text-[9.5px] mono uppercase tracking-[0.14em] px-1 py-px rounded-sm bg-foreground/10">{carrier}</span>
      )}
    </button>
  );
}

// ── Documents cell — collapses Invoice + Pack list + Tracking into one
// flex-row of conditional chips. Shows em-dash if no docs exist.
function BuyerDocumentsCell({ order }) {
  const hasInvoice  = !!order?.invoice?.id;
  const hasPackList = !!order?.id && ["ready_for_pickup", "transit", "delivered"].includes(order.status);
  const hasTracking = !!(order?.shipment?.tracking || order?.tracking);
  if (!hasInvoice && !hasPackList && !hasTracking) {
    return <span className="text-foreground/30 text-xs" title="Documents appear once seller confirms + packs">—</span>;
  }
  return (
    <div className="flex items-center gap-1.5 flex-wrap" data-testid={`buyer-docs-cell-${order.code}`}>
      {hasInvoice  && <InvoiceDocCell invoice={order.invoice}/>}
      {hasPackList && <PackListDocCell order={order}/>}
      {hasTracking && <TrackingDocCell order={order}/>}
    </div>
  );
}


// ─────────────────────────────────────────────────────────────────────────────
// SampleOrdersPreviewBuyer — empty-state preview for new buyers. Mirrors
// the seller-side variant but with viewerRole="buyer" so the drawer shows
// buyer-relevant action buttons (Pay invoice, Track shipment, etc.).
// ─────────────────────────────────────────────────────────────────────────────
function SampleOrdersPreviewBuyer() {
  const [sampleOrder, setSampleOrder] = useState(null);
  return (
    <div data-testid="sample-orders-preview-buyer" className="text-left">
      <SamplePreviewBanner noun="Orders"/>
      <div className="divide-y divide-border">
        {SAMPLE_ORDERS.map(o => {
          const tone =
            o.status === "delivered"  ? "bg-emerald-500/15 text-emerald-700" :
            o.status === "in_transit" ? "bg-blue-500/15 text-blue-700" :
            o.status === "processing" ? "bg-amber-500/15 text-amber-800" :
                                         "bg-foreground/10 text-foreground/65";
          return (
            <button
              type="button"
              key={o.id}
              onClick={() => setSampleOrder(o)}
              className="w-full grid grid-cols-[1fr_1.4fr_120px_90px_110px_140px_120px] gap-3 px-5 py-3 items-center opacity-[0.78] hover:opacity-100 hover:bg-secondary/40 transition-all"
              data-testid={`sample-buyer-order-row-${o.id}`}
            >
              <div className="font-mono text-[12.5px] text-slate-700 flex items-center gap-1.5">
                {o.code}
                <SampleChip />
              </div>
              <div className="text-[13px] text-slate-700 truncate">{o.seller_name}</div>
              <div>
                <span className={`inline-block px-1.5 py-0.5 rounded text-[10px] uppercase tracking-wider ${tone}`}>
                  {o.status.replace(/_/g, " ")}
                </span>
              </div>
              <div className="text-[12px] text-slate-600">{o.units} units</div>
              <div className="text-[13px] font-medium text-slate-700 tabular-nums">${o.total.toLocaleString()}</div>
              <div className="text-[11.5px] text-slate-500">{o.created_at}</div>
              <div className="text-right text-[11px] text-foreground/40">Open →</div>
            </button>
          );
        })}
      </div>
      <SampleOrderDrawer order={sampleOrder} viewerRole="buyer" onClose={() => setSampleOrder(null)} />
    </div>
  );
}


// ─────────────────────────────────────────────────────────────────────────────
// OrderCrqGate — self-contained fetch + render of any pending Fulfillment
// Change Request on this order. Embedded in the Buyer Order Detail drawer
// so the buyer sees the decision UI right next to the order they're
// viewing. Renders nothing when there's no pending CRQ.
// ─────────────────────────────────────────────────────────────────────────────
function OrderCrqGate({ orderId, onDecided }) {
  const [crq, setCrq] = useState(null);
  useEffect(() => {
    if (!orderId) return;
    let alive = true;
    api.get(`/buyer/orders/${orderId}/change-request`)
      .then(r => { if (alive) setCrq(r.data); })
      .catch(() => { if (alive) setCrq(null); });
    return () => { alive = false; };
  }, [orderId]);
  if (!crq || crq.exists === false || crq.status !== "pending") return null;
  return (
    <BuyerChangeRequestBanner
      crq={crq}
      onDecided={() => {
        setCrq(null);
        onDecided?.();
      }}
    />
  );
}

